<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick, reactive, watch } from 'vue'
import { useTrackingStore } from '../stores/trackingStore'
import { useCartStore } from '../stores/cartStore'
import { useSettingsStore } from '../stores/settingsStore'
import { useWishlistStore } from '../stores/wishlistStore'
import { useCompareStore } from '../stores/compareStore'
import { useAuthStore } from '../stores/authStore'
import { usePromotionsStore } from '../stores/promotionsStore'
import { useHomeStore } from '../stores/homeStore'
import { useToast } from '../composables/useToast'
import FeatureIcon from '../components/ui/FeatureIcon.vue'
import ProductCard from '../components/ProductCard.vue'
import { getOptimizedImageUrl } from '../utils/image'
import { saveClickedProductContext } from '../utils/scrollRestoration'

const trackingStore = useTrackingStore()
const cartStore = useCartStore()
const settingsStore = useSettingsStore()
const wishlistStore = useWishlistStore()
const compareStore = useCompareStore()
const authStore = useAuthStore()
const promotionsStore = usePromotionsStore()
const homeStore = useHomeStore()
const { showToast } = useToast()

const formatPrice = (val) => Number(val || 0).toLocaleString('th-TH')

const calculateDiscountPrice = (price, promo) => {
  const p = parseFloat(price || 0)
  if (!promo) return p
  if (promo.discount_type === 'PERCENT') {
    let discount = (p * parseFloat(promo.discount_value || 0)) / 100
    if (promo.max_discount_amount) {
      discount = Math.min(discount, parseFloat(promo.max_discount_amount))
    }
    return Math.max(0, p - discount)
  } else {
    return Math.max(0, p - parseFloat(promo.discount_value || 0))
  }
}

const getItemOriginalPrice = (product) => Number(product.original_price || product.price || 0)

const getItemPromoPrice = (product, promo) => {
  if (product.promo_price !== undefined && product.promo_price !== null && product.promo_price !== '') {
    return Number(product.promo_price)
  }
  return calculateDiscountPrice(product.price, promo)
}

const getItemDiscountPercent = (product, promo) => {
  if (product.discount_percent !== undefined && product.discount_percent !== null && product.discount_percent > 0) {
    return Number(product.discount_percent)
  }
  const orig = getItemOriginalPrice(product)
  if (orig <= 0) return 0
  const promoP = getItemPromoPrice(product, promo)
  const discAmt = Math.max(0, orig - promoP)
  return Number(((discAmt / orig) * 100).toFixed(0))
}

const getPromoDiscountBadgeText = (promo) => {
  if (promo.showcase_products && promo.showcase_products.length > 0) {
    const percentages = promo.showcase_products
      .map(p => getItemDiscountPercent(p, promo))
      .filter(pct => pct > 0)

    if (percentages.length > 0) {
      const minPct = Math.min(...percentages)
      const maxPct = Math.max(...percentages)
      if (minPct === maxPct) {
        return `ส่วนลด ${maxPct}%`
      } else {
        return `ส่วนลด ${minPct}% - ${maxPct}%`
      }
    }
  }

  if (promo.discount_value && Number(promo.discount_value) > 0) {
    return promo.discount_type === 'PERCENT' 
      ? `ส่วนลด ${promo.discount_value}%` 
      : `ส่วนลด ฿${formatPrice(promo.discount_value)}`
  }

  return 'ส่วนลดพิเศษ'
}

const addToCart = async (product) => {
  try {
    await cartStore.addToCart(product, 1);
    showToast(`เพิ่ม ${product.title} ลงในตะกร้าแล้ว`, 'success');
  } catch (err) {
    showToast('ไม่สามารถเพิ่มลงตะกร้าได้', 'error');
  }
}

const toggleWishlistItem = async (e, product) => {
  e.preventDefault();
  e.stopPropagation();
  if (!authStore.isAuthenticated) {
    showToast('กรุณาเข้าสู่ระบบก่อนเพิ่มรายการโปรด', 'warning');
    return;
  }
  const result = await wishlistStore.toggleWishlist(product);
  if (result.success) {
    showToast(result.isAdded ? 'เพิ่มในรายการโปรดแล้ว' : 'นำออกจากรายการโปรดแล้ว', result.isAdded ? 'success' : 'info');
  }
}

const toggleCompareItem = (e, product) => {
  e.preventDefault();
  e.stopPropagation();
  const result = compareStore.toggleCompare(product);
  if (result.success) {
    showToast(result.isAdded ? 'เพิ่มในรายการเปรียบเทียบแล้ว' : 'นำออกจากรายการเปรียบเทียบแล้ว', result.isAdded ? 'success' : 'info');
  } else if (result.error === 'max_reached') {
    showToast('เปรียบเทียบได้สูงสุด 4 ชิ้น', 'warning');
  }
}

const formatArticleDate = (dateString) => {
  if (!dateString) return 'ล่าสุด';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'ล่าสุด';
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch(e) {
    return 'ล่าสุด';
  }
}

// Showcase Slider Drag to Scroll Logic
const showcaseSliderRef = ref(null)
const isDragging = ref(false)
const startX = ref(0)
const scrollLeft = ref(0)
const hasDragged = ref(false)

const startDrag = (e) => {
  isDragging.value = true
  hasDragged.value = false
  startX.value = e.pageX - showcaseSliderRef.value.offsetLeft
  scrollLeft.value = showcaseSliderRef.value.scrollLeft
}

const stopDrag = () => {
  isDragging.value = false
}

const onDrag = (e) => {
  if (!isDragging.value) return
  e.preventDefault()
  const x = e.pageX - showcaseSliderRef.value.offsetLeft
  const walk = (x - startX.value) * 2 // Scroll speed multiplier
  if (Math.abs(walk) > 5) {
    hasDragged.value = true
  }
  showcaseSliderRef.value.scrollLeft = scrollLeft.value - walk
  onShowcaseScroll()
}

const handleSlideClick = (e, targetUrl) => {
  if (hasDragged.value) {
    e.preventDefault()
    e.stopPropagation()
  }
}

const activeShowcaseIndex = ref(1)

const onShowcaseScroll = () => {
  if (!showcaseSliderRef.value) return
  const container = showcaseSliderRef.value
  // Calculate center of the visible area
  const center = container.scrollLeft + container.clientWidth / 2
  
  let minDiff = Infinity
  let activeIdx = 0
  const children = Array.from(container.children).filter(el => el.tagName === 'A')
  
  for (let i = 0; i < children.length; i++) {
    const child = children[i]
    const childCenter = child.offsetLeft + child.clientWidth / 2
    const diff = Math.abs(center - childCenter)
    if (diff < minDiff) {
      minDiff = diff
      activeIdx = i
    }
  }
  activeShowcaseIndex.value = activeIdx
}

const trackCTA = (action) => {
  trackingStore.trackEvent({ type: 'click_cta', action })
}

const getSlideFeatures = (slide) => {
  if (slide && slide.features && Array.isArray(slide.features) && slide.features.length === 3) {
    return slide.features;
  }
  return [
    { icon: 'shield', title: 'วัสดุคุณภาพ', desc: 'แข็งแรง ทนทาน' },
    { icon: 'sun', title: 'กันแดด กันฝน', desc: 'ทนทุกสภาพอากาศ' },
    { icon: 'wrench', title: 'ประกอบง่าย', desc: 'รวดเร็ว ไม่ยุ่งยาก' }
  ];
};

const getSlideHighlights = (slide) => {
  if (slide && slide.highlights && Array.isArray(slide.highlights) && slide.highlights.length === 3) {
    return slide.highlights;
  }
  return [
    { icon: 'sun', label: 'จุดเด่น', title: 'กรองแสง UV ถนอมพืชพรรณ' },
    { icon: 'leaf', label: 'ความพิเศษ', title: 'วัสดุเป็นมิตรต่อต้นไม้' },
    { icon: 'wind', label: 'คุณสมบัติ', title: 'โครงสร้างต้านลมพายุ' }
  ];
};

const getHighlightPositionClass = (index) => {
  switch (index) {
    case 0:
      return 'top-[8%] left-[-4%] sm:left-[-8%]';
    case 1:
      return 'top-[45%] right-[-4%] sm:right-[-8%]';
    case 2:
      return 'bottom-[12%] right-[4%] sm:right-[2%]';
    default:
      return '';
  }
};




function parseBooleanSetting(val, defaultVal = false) {
  if (val === undefined || val === null || val === '') return defaultVal
  if (typeof val === 'boolean') return val
  if (val === 'true' || val === 1 || val === '1') return true
  if (val === 'false' || val === 0 || val === '0') return false
  return defaultVal
}

// Home State from Settings API
const settingsLoading = ref(false)
const slides = ref([])
const currentSlide = ref(0)
const slideProgress = ref(0)
let progressInterval = null
const stats = ref([])
const homeShowHighlightCategories = ref(true)
const featuresHeading = ref('')
const featuresTitle = ref('')
const featuresDesc = ref('')
const featuresItems = ref([])
const featuresImage = ref('')
const featuresBadgeTitle = ref('')
const featuresBadgeDesc = ref('')
const testimonials = ref([])
const partners = ref([])
const homeShowTestimonials = ref(false)
const homeShowPartners = ref(false)
const homeShowAffiliates = ref(false)
const homeShowFaq = ref(false)
const homeShowShowcase = ref(true)
const homeShowStats = ref(false)
const homeShowFeatures = ref(false)
const homeShowHowItWorks = ref(false)
const ctaTitle = ref('')
const ctaDesc = ref('')
const ctaButtonText = ref('')
const ctaButtonLink = ref('')

const repeatedPartnersForMarquee = computed(() => {
  if (!partners.value || partners.value.length === 0) return []
  // Make sure we have enough items to span the screen smoothly
  // We duplicate the original array multiple times to form a long sequence
  const base = [...partners.value, ...partners.value, ...partners.value, ...partners.value]
  // We double it so the marquee can shift by -50% perfectly seamlessly
  return [...base, ...base]
})

const recentProjects = ref([])
const activeProjectIndex = ref(0)
const featuredProject = computed(() => recentProjects.value[activeProjectIndex.value] || recentProjects.value[0] || null)
const recentArticles = ref([])
const categories = ref([])
const categoryShowcaseSettings = ref([])
const showcaseCategoriesData = ref([])
const expandedShowcases = ref({})
const homeHighlightSettings = ref(null)
const highlightCategoriesList = ref([])
const homeShowcaseSlider = ref([])
const showcaseProducts = ref([])
const affiliatedCompanies = ref([])

const bentoCards = ref([])
const trustBadges = ref([])
const isLifestyleMenuOpen = ref(false)
const lifestyleItems = ref([])

const getLifestyleIconSvg = (icon) => {
  const key = String(icon || '').toLowerCase()
  if (key.includes('seat') || key.includes('นั่ง') || key.includes('chair') || key.includes('campsite') || key === 'life-1') {
    // Lounger / patio seat
    return 'M4 19l4-11h8l4 11M4 19h16M7 11h10'
  }
  if (key.includes('furn') || key.includes('เฟอร์') || key.includes('sofa') || key === 'life-2') {
    // Modern outdoor sofa / armchair
    return 'M4 10V6a2 2 0 012-2h12a2 2 0 012 2v4M4 14v4M20 14v4M3 10h18v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4z'
  }
  if (key.includes('stor') || key.includes('กล่อง') || key.includes('box') || key === 'life-3') {
    // Storage chest / deck box with lid
    return 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'
  }
  if (key.includes('gaze') || key.includes('ศาลา') || key.includes('ร่ม') || key === 'life-4') {
    // Gazebo / pavilion roof
    return 'M3 10L12 3l9 7v10a1 1 0 01-1 1H4a1 1 0 01-1-1V10zM9 21v-7h6v7'
  }
  if (key.includes('camp') || key.includes('เต็นท์') || key.includes('tent') || key === 'life-5') {
    // A-frame tent
    return 'M12 3L2 21h20L12 3zM12 3v18M7.5 15h9'
  }
  if (key.includes('gard') || key.includes('สวน') || key.includes('plant') || key === 'life-6') {
    // Garden plant / flower
    return 'M12 21a9 9 0 009-9c0-4.97-4.03-9-9-9-4.97 0-9 4.03-9 9 0 4.97 4.03 9 9 9zm0 0v-8m0 0a4 4 0 014-4m-4 4a4 4 0 00-4-4'
  }
  if (key.includes('tool') || key.includes('เครื่องมือ') || key.includes('wrench') || key === 'life-7') {
    // Garden trowel / shears
    return 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z'
  }
  if (key.includes('spec') || key.includes('โปร') || key.includes('tag') || key === 'life-8') {
    // Price tag
    return 'M7 7h.01M3 11l8.586-8.586a2 2 0 012.828 0l6.586 6.586a2 2 0 010 2.828L12.414 20.414a2 2 0 01-2.828 0L3 13.828A2 2 0 012.414 12.414L3 11z'
  }
  if (key.includes('brand') || key.includes('แบรนด์') || key.includes('star') || key === 'life-9') {
    // Star / award badge
    return 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z'
  }
  // Default clean tree outline
  return 'M12 2L4 12h3l-2 5h4v5h2v-5h4l-2-5h3L12 2z'
}

const secondaryCategories = ref([])
const faqItems = ref([])
const howItWorksSteps = ref([])

const sectionTitles = ref({
  categoriesTitle: 'คอลเลกชัน',
  categoriesTitleHighlight: 'พื้นที่เก็บของ',
  categoriesDesc: 'เลือกดูหมวดหมู่สินค้าที่ออกแบบมาเพื่อตอบโจทย์ทุกพื้นที่ของคุณ',
  collectionTitle: 'รุ่นยอดนิยม',
  collectionDesc: 'สัมผัสการออกแบบที่ขายดีที่สุด การันตีคุณภาพและความสวยงามเกินราคา',
  showcaseBadge: 'คอลเลกชันยอดฮิตสินค้าแนะนำ',
  showcaseTitle: 'สินค้าแนะนำ',
  showcaseTitleHighlight: 'ที่ออกแบบเป็นพิเศษ',
  showcaseViewAllText: 'ดูรุ่นทั้งหมด (100+)',
  howItWorksTitle: 'ขั้นตอนการบริการ',
  howItWorksHeading: 'ง่ายแค่ 4 ขั้นตอน พร้อมใช้งานทันที',
  howItWorksDesc: 'จากปรึกษาตัวเองถึงติดตั้งเสร็จสมบูรณ์ เราดูแลทุกขั้นตอนอย่างมืออาชีพ',
  projectsTitle: 'ผลงานของเรา',
  projectsHeading: 'ภาพผลงานติดตั้งจริงล่าสุด',
  testimonialsTitle: 'เสียงตอบรับจากลูกค้า',
  testimonialsHeading: 'ความไว้วางใจจากตัวจริง',
  partnersTitle: 'พาร์ทเนอร์ของเรา',
  partnersHeading: 'ความไว้วางใจจากองค์กรชั้นนำ',
  articlesTitle: 'คลังสาระ',
  articlesHeading: 'อัพเดทข่าวสารน่ารู้',
  faqTitle: 'คำถามที่พบบ่อย',
  faqHeading: 'สิ่งที่คุณอยากรู้',
  faqDesc: 'รวบรวมคำตอบสำหรับคำถามที่ลูกค้าสอบถามบ่อยที่สุด',
  affiliatesTitle: 'บริษัทในเครือ',
  affiliatesHeading: 'เครือข่ายธุรกิจของเรา',
})

// Hydrate immediately from homeStore if already cached (preserves DOM height & enables instant back navigation)
if (homeStore.isLoaded) {
  slides.value = homeStore.slides || []
  stats.value = homeStore.stats || []
  featuresItems.value = homeStore.featuresItems || []
  testimonials.value = homeStore.testimonials || []
  partners.value = homeStore.partners || []
  affiliatedCompanies.value = homeStore.affiliatedCompanies || []
  categoryShowcaseSettings.value = homeStore.categoryShowcaseSettings || []
  homeShowcaseSlider.value = homeStore.homeShowcaseSlider || []
  homeHighlightSettings.value = homeStore.homeHighlightSettings || null
  faqItems.value = homeStore.faqItems || []
  howItWorksSteps.value = homeStore.howItWorksSteps || []
  sectionTitles.value = { ...sectionTitles.value, ...(homeStore.sectionTitles || {}) }
  featuresHeading.value = homeStore.featuresHeading || ''
  featuresTitle.value = homeStore.featuresTitle || ''
  featuresDesc.value = homeStore.featuresDesc || ''
  featuresImage.value = homeStore.featuresImage || ''
  featuresBadgeTitle.value = homeStore.featuresBadgeTitle || ''
  featuresBadgeDesc.value = homeStore.featuresBadgeDesc || ''
  ctaTitle.value = homeStore.ctaTitle || ''
  ctaDesc.value = homeStore.ctaDesc || ''
  ctaButtonText.value = homeStore.ctaButtonText || ''
  ctaButtonLink.value = homeStore.ctaButtonLink || ''
  homeShowTestimonials.value = Boolean(homeStore.homeShowTestimonials)
  homeShowPartners.value = Boolean(homeStore.homeShowPartners)
  homeShowAffiliates.value = Boolean(homeStore.homeShowAffiliates)
  homeShowFaq.value = Boolean(homeStore.homeShowFaq)
  homeShowShowcase.value = homeStore.homeShowShowcase !== false
  homeShowStats.value = Boolean(homeStore.homeShowStats)
  homeShowFeatures.value = Boolean(homeStore.homeShowFeatures)
  homeShowHowItWorks.value = Boolean(homeStore.homeShowHowItWorks)
  categories.value = homeStore.categories || []
  showcaseProducts.value = homeStore.showcaseProducts || []
  recentProjects.value = homeStore.recentProjects || []
  recentArticles.value = homeStore.recentArticles || []
  highlightCategoriesList.value = homeStore.highlightCategoriesList || []
  showcaseCategoriesData.value = homeStore.showcaseCategoriesData || []
  bentoCards.value = homeStore.bentoCards || []
  trustBadges.value = homeStore.trustBadges || []
  lifestyleItems.value = homeStore.lifestyleItems || []
  secondaryCategories.value = homeStore.secondaryCategories || []
  settingsLoading.value = false
}

// --- Cinematic Bento Showcase Slider State ---
const showcaseActiveIndex = ref(0)
const showcaseProgress = ref(0)
const showcaseIsPlaying = ref(true)
let showcaseProgressInterval = null
const SHOWCASE_DURATION = 6000 // 6 seconds
const SHOWCASE_TICK = 250 // 250ms (greatly reduces CPU cycles & prevents tab freeze)

const showcaseItemsList = computed(() => {
  const list = homeShowcaseSlider.value && homeShowcaseSlider.value.length > 0 
    ? homeShowcaseSlider.value 
    : showcaseProducts.value
  return list || []
})

const getSpecsForItem = (item) => {
  if (!item) return []
  if (item.specs && Array.isArray(item.specs) && item.specs.length > 0) {
    return item.specs
  }
  const categoryName = item.product?.category || item.subtitle || '';
  if (categoryName.includes('พลาสติก PP')) {
    return [
      { label: 'วัสดุหลัก', value: 'พลาสติก PP พรีเมียม' },
      { label: 'ความหนา', value: '12 มม. กันรังสี UV' },
      { label: 'โครงสร้าง', value: 'เหล็กเคลือบกันสนิม' },
      { label: 'มาตรฐานการผลิต', value: 'เกรดส่งออกพรีเมียม' }
    ]
  } else if (categoryName.includes('พลาสติก HDPE')) {
    return [
      { label: 'วัสดุหลัก', value: 'พลาสติก HDPE ทนทานสูง' },
      { label: 'ผิวสัมผัส', value: 'ลายไม้ธรรมชาติพรีเมียม' },
      { label: 'การกันน้ำ', value: '100% กันฝนกรด' },
      { label: 'มาตรฐานการผลิต', value: 'เกรดส่งออกพรีเมียม' }
    ]
  } else if (categoryName.includes('โลหะ')) {
    return [
      { label: 'วัสดุหลัก', value: 'เหล็ก Galvalume USA' },
      { label: 'เทคโนโลยีสี', value: 'อบความร้อนสี่ชั้น' },
      { label: 'ความต้านทานลม', value: 'สูงสุด 120 กม./ชม.' },
      { label: 'มาตรฐานการผลิต', value: 'เกรดส่งออกพรีเมียม' }
    ]
  } else if (categoryName.includes('โรงเรือน') || categoryName.includes('กรีนเฮาส์')) {
    return [
      { label: 'วัสดุคลุม', value: 'PE Mesh กันรังสี UV' },
      { label: 'โครงเหล็ก', value: 'ชุบกัลวาไนซ์กันสนิม' },
      { label: 'การระบายอากาศ', value: 'หน้าต่างตาข่ายรอบทิศ' },
      { label: 'การติดตั้ง', value: 'ง่ายด้วยน็อคดาวน์' }
    ]
  }
  // Default specs for custom slides or others
  return [
    { label: 'โครงสร้างหลัก', value: 'เกรดพรีเมียม USA' },
    { label: 'ความทนทาน', value: 'ทนทุกสภาวะอากาศ' },
    { label: 'ฟังก์ชัน', value: 'มินิมอล ประหยัดพื้นที่' },
    { label: 'มาตรฐานการผลิต', value: 'เกรดส่งออกพรีเมียม' }
  ]
}

const getCategoryShowcaseMeta = (category, index = 0) => {
  const name = (category?.name || '').toLowerCase()
  const num = String(index + 1).padStart(2, '0')
  if (name.includes('hdpe')) {
    return {
      indexLabel: num,
      subtitle: category?.description || 'โครงสร้างพลาสติก HDPE หนาพิเศษ ผิวสัมผัสลายไม้ธรรมชาติ ทนแดด ทนฝน ไม่กรอบแตก',
      badges: ['พลาสติก HDPE เกรดพรีเมียม', 'กันรังสี UV 100%', 'โครงสร้าง 2 ชั้นแข็งแรง']
    }
  }
  if (name.includes('pp')) {
    return {
      indexLabel: num,
      subtitle: category?.description || 'บ้านเก็บของพลาสติก PP ดีไซน์มินิมอล น้ำหนักเบา ประกอบง่าย ทนความชื้น 100%',
      badges: ['พลาสติก PP คุณภาพสูง', 'กันน้ำ 100%', 'ดูแลรักษาง่าย']
    }
  }
  if (name.includes('โลหะ') || name.includes('เมทัล')) {
    return {
      indexLabel: num,
      subtitle: category?.description || 'โครงสร้างเหล็กกัลวาไนซ์เคลือบสีอบความร้อน ทนทานต่อทุกสภาพอากาศ รองรับงานหนัก',
      badges: ['เหล็กกัลวาไนซ์กันสนิม', 'สีอบความร้อน 4 ชั้น', 'แข็งแรงพิเศษ']
    }
  }
  if (name.includes('โรงเรือน')) {
    return {
      indexLabel: num,
      subtitle: category?.description || 'โรงเรือนสำเร็จรูปพร้อมโครงเหล็กชุบกัลวาไนซ์ ระบายอากาศดีเยี่ยม ปกป้องต้นไม้ทุกฤดูกาล',
      badges: ['โครงกัลวาไนซ์ปลอดสนิม', 'กรองแสง UV', 'ระบายอากาศรอบทิศ']
    }
  }
  return {
    indexLabel: num,
    subtitle: category?.description || 'นวัตกรรมพื้นที่จัดเก็บกลางแจ้งเกรดพรีเมียม แข็งแรง ทนทาน ตอบโจทย์ทุกพื้นที่รอบบ้าน',
    badges: ['วัสดุเกรดพรีเมียม', 'ทนแดด ทนฝน 100%', 'ประกอบติดตั้งง่าย']
  }
}

const startShowcaseProgress = () => {
  clearInterval(showcaseProgressInterval)
  showcaseProgress.value = 0
  showcaseProgressInterval = setInterval(() => {
    if (showcaseIsPlaying.value) {
      showcaseProgress.value += (SHOWCASE_TICK / SHOWCASE_DURATION) * 100
      if (showcaseProgress.value >= 100) {
        showcaseProgress.value = 0
        const itemsCount = showcaseItemsList.value.length
        if (itemsCount > 0) {
          showcaseActiveIndex.value = (showcaseActiveIndex.value + 1) % itemsCount
        }
      }
    }
  }, SHOWCASE_TICK)
}

const stopShowcaseProgress = () => {
  clearInterval(showcaseProgressInterval)
}

const toggleShowcasePlay = () => {
  showcaseIsPlaying.value = !showcaseIsPlaying.value
}

const selectShowcaseSlide = (idx) => {
  showcaseActiveIndex.value = idx
  showcaseProgress.value = 0
}

const nextShowcaseSlide = () => {
  const itemsCount = showcaseItemsList.value.length
  if (itemsCount > 0) {
    showcaseActiveIndex.value = (showcaseActiveIndex.value + 1) % itemsCount
    showcaseProgress.value = 0
  }
}

const prevShowcaseSlide = () => {
  const itemsCount = showcaseItemsList.value.length
  if (itemsCount > 0) {
    showcaseActiveIndex.value = (showcaseActiveIndex.value - 1 + itemsCount) % itemsCount
    showcaseProgress.value = 0
  }
}

let slideInterval = null

// Load Homepage Settings
const loadHomepageSettings = async () => {
  if (!homeStore.isLoaded) {
    settingsLoading.value = true
  }
  try {
    const res = await fetch('/api/settings')
    const data = await res.json()
    if (data.success && data.data) {
      const s = data.data
      settingsStore.initializeSettings(s)
      
      try { 
        const parsedSlides = s.home_slides ? JSON.parse(s.home_slides) : [] 
        slides.value = Array.isArray(parsedSlides) ? parsedSlides : []
      } catch(e) {
        slides.value = []
      }
      
      if (slides.value.length > 0) {
        slides.value = slides.value.map((slide, i) => {
          if (typeof slide !== 'object' || !slide) return { image: '' }
          
          const enriched = {
            cta2Text: 'ปรึกษาฟรีกับ AI',
            cta2Link: '/ai-consultant',
            showCta2: true,
            showHighlights: true,
            showFeatures: true,
            features: [
              { icon: 'shield', title: 'วัสดุคุณภาพ', desc: 'แข็งแรง ทนทาน' },
              { icon: 'sun', title: 'กันแดด กันฝน', desc: 'ทนทุกสภาพอากาศ' },
              { icon: 'wrench', title: 'ประกอบง่าย', desc: 'รวดเร็ว ไม่ยุ่งยาก' }
            ],
            highlights: [
              { icon: 'sun', label: 'จุดเด่น', title: 'กรองแสง UV ถนอมพืชพรรณ' },
              { icon: 'leaf', label: 'ความพิเศษ', title: 'วัสดุเป็นมิตรต่อต้นไม้' },
              { icon: 'wind', label: 'คุณสมบัติ', title: 'โครงสร้างต้านลมพายุ' }
            ],
            ...slide
          }

          if (!enriched.features || !Array.isArray(enriched.features) || enriched.features.length === 0) {
            enriched.features = [
              { icon: 'shield', title: 'วัสดุคุณภาพ', desc: 'แข็งแรง ทนทาน' },
              { icon: 'sun', title: 'กันแดด กันฝน', desc: 'ทนทุกสภาพอากาศ' },
              { icon: 'wrench', title: 'ประกอบง่าย', desc: 'รวดเร็ว ไม่ยุ่งยาก' }
            ]
          }
          if (!enriched.highlights || !Array.isArray(enriched.highlights) || enriched.highlights.length === 0) {
            enriched.highlights = [
              { icon: 'sun', label: 'จุดเด่น', title: 'กรองแสง UV ถนอมพืชพรรณ' },
              { icon: 'leaf', label: 'ความพิเศษ', title: 'วัสดุเป็นมิตรต่อต้นไม้' },
              { icon: 'wind', label: 'คุณสมบัติ', title: 'โครงสร้างต้านลมพายุ' }
            ]
          }

          return {
            ...enriched,
            image: (slide.image && typeof slide.image === 'string') ? slide.image : ''
          }
        })
      }
      try { 
        const parsedStats = s.home_stats ? JSON.parse(s.home_stats) : null
        if (parsedStats && Array.isArray(parsedStats) && parsedStats.length > 0) stats.value = parsedStats
      } catch(e) {}
      try { 
        const parsedFeatures = s.home_features_items ? JSON.parse(s.home_features_items) : null
        if (parsedFeatures && Array.isArray(parsedFeatures) && parsedFeatures.length > 0) featuresItems.value = parsedFeatures
      } catch(e) {}
      try { 
        const parsedTestis = s.home_testimonials ? JSON.parse(s.home_testimonials) : null
        if (parsedTestis && Array.isArray(parsedTestis)) {
          testimonials.value = parsedTestis.map(t => ({
            name: t.name || '',
            role: t.role || '',
            comment: t.comment || t.text || '',
            rating: Number(t.rating || t.stars || 5),
            avatar: t.avatar || ''
          }))
        } else {
          testimonials.value = []
        }
      } catch(e) {}
      try { 
        const parsedPartners = s.home_partners ? JSON.parse(s.home_partners) : null
        if (parsedPartners && Array.isArray(parsedPartners)) {
          partners.value = parsedPartners.map(p => ({
            name: p.name || '',
            logo: p.logo || p.logo_url || '',
            link: p.link || '#'
          }))
        } else {
          partners.value = []
        }
      } catch(e) {}
      try { 
        const parsedAffiliates = s.home_affiliates ? JSON.parse(s.home_affiliates) : []
        affiliatedCompanies.value = Array.isArray(parsedAffiliates) ? parsedAffiliates : []
      } catch(e) {}
      try { 
        const parsedShowcase = s.home_category_showcase ? JSON.parse(s.home_category_showcase) : []
        categoryShowcaseSettings.value = Array.isArray(parsedShowcase) ? parsedShowcase : []
      } catch(e) {}
      try { 
        const parsedShowcaseSlider = s.home_showcase_slider ? JSON.parse(s.home_showcase_slider) : []
        homeShowcaseSlider.value = Array.isArray(parsedShowcaseSlider) ? parsedShowcaseSlider : []
      } catch(e) {}
      try { 
        const parsedHighlights = s.home_highlight_categories ? JSON.parse(s.home_highlight_categories) : null
        homeHighlightSettings.value = (parsedHighlights && typeof parsedHighlights === 'object') ? parsedHighlights : null
      } catch(e) {}
      try {
        const parsedFaq = s.home_faq ? JSON.parse(s.home_faq) : null
        if (parsedFaq && parsedFaq.length > 0) faqItems.value = parsedFaq
      } catch(e) {}
      try {
        const parsedHowItWorks = s.home_how_it_works ? JSON.parse(s.home_how_it_works) : null
        if (parsedHowItWorks && parsedHowItWorks.length > 0) howItWorksSteps.value = parsedHowItWorks
      } catch(e) {}
      try {
        const parsedTitles = s.home_section_titles ? JSON.parse(s.home_section_titles) : null
        if (parsedTitles && Object.keys(parsedTitles).length > 0) sectionTitles.value = { ...sectionTitles.value, ...parsedTitles }
      } catch(e) {}
      try {
        if (s.home_bento_cards) {
          const parsedBento = JSON.parse(s.home_bento_cards)
          if (Array.isArray(parsedBento) && parsedBento.length > 0) bentoCards.value = parsedBento
        }
      } catch(e) {}
      try {
        if (s.home_trust_badges) {
          const parsedTrust = JSON.parse(s.home_trust_badges)
          if (Array.isArray(parsedTrust) && parsedTrust.length > 0) trustBadges.value = parsedTrust
        }
      } catch(e) {}
      try {
        if (s.home_lifestyle_items) {
          const parsedLife = JSON.parse(s.home_lifestyle_items)
          if (Array.isArray(parsedLife) && parsedLife.length > 0) lifestyleItems.value = parsedLife
        }
      } catch(e) {}
      try {
        if (s.home_secondary_categories) {
          const parsedSec = JSON.parse(s.home_secondary_categories)
          if (Array.isArray(parsedSec) && parsedSec.length > 0) secondaryCategories.value = parsedSec
        }
      } catch(e) {}

      featuresHeading.value = s.home_features_heading || ''
      featuresTitle.value = s.home_features_title || ''
      featuresDesc.value = s.home_features_desc || ''
      const rawFeatImg = s.home_features_image || ''
      featuresImage.value = rawFeatImg ? getOptimizedImageUrl(rawFeatImg) : ''
      featuresBadgeTitle.value = s.home_features_badge_title || ''
      featuresBadgeDesc.value = s.home_features_badge_desc || ''

      ctaTitle.value = s.home_cta_title || ''
      ctaDesc.value = s.home_cta_desc || ''
      ctaButtonText.value = s.home_cta_button_text || ''
      ctaButtonLink.value = s.home_cta_button_link || ''

      // Section visibility toggles
      homeShowTestimonials.value = parseBooleanSetting(s.home_show_testimonials, false)
      homeShowPartners.value = parseBooleanSetting(s.home_show_partners, false)
      homeShowAffiliates.value = parseBooleanSetting(s.home_show_affiliates, false)
      homeShowFaq.value = parseBooleanSetting(s.home_show_faq, false)
      homeShowShowcase.value = parseBooleanSetting(s.home_show_showcase, true)
      homeShowStats.value = parseBooleanSetting(s.home_show_stats, false)
      homeShowFeatures.value = parseBooleanSetting(s.home_show_features, false)
      homeShowHowItWorks.value = parseBooleanSetting(s.home_show_how_it_works, false)
      
    }
  } catch (error) {
    console.error('Failed to load homepage settings:', error)
  } finally {
    settingsLoading.value = false
    
    // Start slider if we have multiple slides
    if (slides.value.length > 1 && !slideInterval) {
      startSlideTimer()
    }
  }
}

const SLIDE_DURATION = 5000 // 5 seconds per slide

const startSlideTimer = () => {
  stopSlideTimer()
  if (slides.value.length > 1) {
    slideInterval = setInterval(() => {
      currentSlide.value = (currentSlide.value + 1) % slides.value.length
    }, SLIDE_DURATION)
  }
}

const stopSlideTimer = () => {
  if (slideInterval) {
    clearInterval(slideInterval)
    slideInterval = null
  }
}

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % slides.value.length
  startSlideTimer()
}

const prevSlide = () => {
  currentSlide.value = (currentSlide.value - 1 + slides.value.length) % slides.value.length
  startSlideTimer()
}

const goToSlide = (index) => {
  currentSlide.value = index
  startSlideTimer()
}

// Add Structured Data (JSON-LD)
const addStructuredData = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": settingsStore.storeName || "Outdoor Lander",
    "image": settingsStore.storeLogo ? (settingsStore.storeLogo.startsWith('http') ? settingsStore.storeLogo : `${window.location.origin}${settingsStore.storeLogo}`) : `${window.location.origin}/logo.png`,
    "@id": "https://outdoorlander.com",
    "url": "https://outdoorlander.com",
    "telephone": settingsStore.storePhone || "02-9081348-9",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "75/110 หมู่ 11 ตำบลคลองหนึ่ง",
      "addressLocality": "อำเภอคลองหลวง",
      "addressRegion": "ปทุมธานี",
      "postalCode": "12120",
      "addressCountry": "TH"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
      ],
      "opens": "08:30",
      "closes": "17:30"
    }
  }

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.text = JSON.stringify(schema)
  script.id = 'json-ld-localbusiness'
  document.head.appendChild(script)
}

// FAQ Actions
const toggleFaq = (index) => {
  faqItems.value[index].open = !faqItems.value[index].open
}

const addFaqSchema = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.value.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  }
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.text = JSON.stringify(faqSchema)
  script.id = 'json-ld-faq'
  document.head.appendChild(script)
}


const totalSlides = computed(() => {
  const list = homeShowcaseSlider.value && homeShowcaseSlider.value.length > 0 
    ? homeShowcaseSlider.value 
    : showcaseProducts.value
  return list ? list.length : 0
})

const scrollToSlide = (idx) => {
  if (!showcaseSliderRef.value) return
  const container = showcaseSliderRef.value
  const children = Array.from(container.children).filter(el => el.tagName === 'A')
  if (children[idx]) {
    container.scrollTo({
      left: children[idx].offsetLeft - container.offsetLeft,
      behavior: 'smooth'
    })
    activeShowcaseIndex.value = idx
  }
}

const fetchShowcaseProducts = async () => {
  try {
    const res = await fetch('/api/products')
    const data = await res.json()
    if (data.success) {
      showcaseProducts.value = data.data.slice(0, 6).map((p, index) => ({
        id: p.id,
        slug: p.slug,
        sku: p.sku,
        title: p.name,
        image: p.image_url || '/images/placeholder.png',
        subtitle: p.sku ? `รุ่น ${p.sku}` : (p.category || 'Premium Unit'),
        price: p.price,
        originalPrice: p.original_price,
        is_out_of_stock: p.is_out_of_stock || false,
        isBestSeller: false,
        isHiddenMobile: '',
        card_features: p.card_features || null,
        compare_enabled: p.compare_enabled !== false
      }))

      // Enrich homeShowcaseSlider with product data
      if (homeShowcaseSlider.value && homeShowcaseSlider.value.length > 0) {
        homeShowcaseSlider.value = homeShowcaseSlider.value.map(slide => {
          const product = slide.productId ? data.data.find(p => p.id === parseInt(slide.productId)) : null
          return { ...slide, product }
        })
      }
    }
  } catch (err) {
    console.error('Failed to fetch showcase products', err)
  }
}

const fetchRecentContent = async () => {
  try {
    const [projRes, artRes] = await Promise.all([
      fetch('/api/projects/published?limit=4'),
      fetch('/api/articles/published?limit=3')
    ])
    
    const projData = await projRes.json()
    const artData = await artRes.json()
    
    if (projData.success) {
      recentProjects.value = projData.data.slice(0, 4)
    }
    
    if (artData.success) {
      recentArticles.value = artData.data.slice(0, 3)
    }
  } catch (err) {
    console.error('Failed to fetch recent content', err)
  }
}

const fetchCategories = async () => {
  try {
    const res = await fetch('/api/categories')
    const data = await res.json()
    if (data.success) {
      const priority = [
        'บ้านเก็บของพลาสติก PP',
        'บ้านเก็บของพลาสติก HDPE',
        'บ้านเก็บของโลหะ',
        'โรงเรือนปลูกพืช',
        'กล่องเก็บของกลางแจ้ง'
      ];
      // Sort categories: Priority first (in exact order), then the rest
      const sorted = data.data.sort((a, b) => {
        const indexA = priority.indexOf(a.name);
        const indexB = priority.indexOf(b.name);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return 0;
      });
      categories.value = sorted;

      // Initialize highlight Categories list
      if (homeHighlightSettings.value && homeHighlightSettings.value.items && homeHighlightSettings.value.items.some(i => i.categoryId)) {
        const configuredCats = homeHighlightSettings.value.items
          .map(item => {
            const cat = categories.value.find(c => String(c.id) === String(item.categoryId));
            if (!cat) return null;
            return {
              ...cat,
              customDescription: item.customDesc || cat.description,
              productIds: item.productIds || []
            }
          })
          .filter(Boolean);
          
        const usedIds = configuredCats.map(c => String(c.id));
        let fallbackIndex = 0;
        while (configuredCats.length < 5 && fallbackIndex < categories.value.length) {
          const fallbackCat = categories.value[fallbackIndex];
          if (!usedIds.includes(String(fallbackCat.id))) {
            configuredCats.push({
              ...fallbackCat,
              customDescription: fallbackCat.description,
              productIds: []
            });
            usedIds.push(String(fallbackCat.id));
          }
          fallbackIndex++;
        }
        highlightCategoriesList.value = configuredCats;
      } else {
        // Fallback to top 5 categories
        highlightCategoriesList.value = categories.value.slice(0, 5).map(cat => ({
          ...cat,
          customDescription: cat.description,
          productIds: []
        }));
      }
    }
  } catch (err) {
    console.error('Failed to fetch categories', err)
  }
}

const buildShowcaseCategoriesData = async () => {
  if (!categoryShowcaseSettings.value || categoryShowcaseSettings.value.length === 0 || categories.value.length === 0) return;
  
  // Fetch all active products once to guarantee we have full data and accurate matching
  let allProds = [];
  try {
    const res = await fetch('/api/products');
    const data = await res.json();
    if (data.success && Array.isArray(data.data)) {
      allProds = data.data;
    }
  } catch (e) {
    console.error('Error fetching products for category showcase:', e);
  }

  const formattedData = [];
  
  for (const showcase of categoryShowcaseSettings.value) {
    const category = categories.value.find(c => 
      String(c.id) === String(showcase.categoryId) || 
      String(c.name).trim().toLowerCase() === String(showcase.categoryId).trim().toLowerCase()
    );
    if (!category) continue;
    
    let products = [];
    const pinnedIds = Array.isArray(showcase.productIds) ? showcase.productIds : [];
    
    // 1. If admin specifically pinned and ordered products, retrieve them in the EXACT pinned order
    if (pinnedIds.length > 0) {
      pinnedIds.forEach(pid => {
        const found = allProds.find(p => String(p.id) === String(pid));
        if (found && !products.some(p => String(p.id) === String(found.id))) {
          products.push(found);
        }
      });
    } else {
      // 2. If no products were pinned, fall back to active products of this category sorted by sort_order
      const catIdStr = String(category.id);
      const catName = String(category.name).trim().toLowerCase();
      
      const catProducts = allProds.filter(p => {
        if (p.category !== undefined && p.category !== null) {
          const pCatStr = String(p.category).trim().toLowerCase();
          if (pCatStr === catIdStr || pCatStr === catName) return true;
        }
        if (p.category_name && String(p.category_name).trim().toLowerCase() === catName) return true;
        if (Array.isArray(p.categories)) {
          if (p.categories.some(c => String(c).trim().toLowerCase() === catIdStr || String(c).trim().toLowerCase() === catName)) return true;
        }
        return false;
      });
      
      catProducts.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || b.id - a.id);
      products = catProducts.slice(0, 4);
    }
    
    // If no active products found for this block, skip to avoid empty rendering
    if (products.length === 0) continue;
    
    let parsedFeatures = { enabled: true, items: [] };
    try {
      if (typeof category.features === 'string') {
        const parsed = JSON.parse(category.features);
        if (Array.isArray(parsed)) {
          parsedFeatures.items = parsed || [];
        } else if (parsed && typeof parsed === 'object') {
          parsedFeatures.enabled = parsed.enabled !== false;
          parsedFeatures.items = Array.isArray(parsed.items) ? parsed.items : [];
        }
      } else if (Array.isArray(category.features)) {
        parsedFeatures.items = [...category.features];
      } else if (category.features && typeof category.features === 'object') {
        parsedFeatures.enabled = category.features.enabled !== false;
        parsedFeatures.items = Array.isArray(category.features.items) ? category.features.items : [];
      }
    } catch (e) {}

    formattedData.push({
      category: { ...category, features: parsedFeatures },
      products: products.map(p => ({
        ...p,
        id: p.id,
        slug: p.slug,
        sku: p.sku,
        name: p.name,
        title: p.name,
        image: p.image_url || '/images/placeholder.png',
        image_url: p.image_url || '/images/placeholder.png',
        subtitle: p.sku ? `รุ่น: ${p.sku}` : (p.category || 'Premium Unit'),
        price: p.price,
        originalPrice: p.original_price,
        original_price: p.original_price,
        is_out_of_stock: p.is_out_of_stock || false,
        card_features: p.card_features || null,
        compare_enabled: p.compare_enabled !== false
      }))
    });
  }
  
  showcaseCategoriesData.value = formattedData;
}

const syncToHomeStore = () => {
  homeStore.slides = slides.value
  homeStore.stats = stats.value
  homeStore.featuresItems = featuresItems.value
  homeStore.testimonials = testimonials.value
  homeStore.partners = partners.value
  homeStore.affiliatedCompanies = affiliatedCompanies.value
  homeStore.categoryShowcaseSettings = categoryShowcaseSettings.value
  homeStore.homeShowcaseSlider = homeShowcaseSlider.value
  homeStore.homeHighlightSettings = homeHighlightSettings.value
  homeStore.faqItems = faqItems.value
  homeStore.howItWorksSteps = howItWorksSteps.value
  homeStore.sectionTitles = sectionTitles.value
  homeStore.featuresHeading = featuresHeading.value
  homeStore.featuresTitle = featuresTitle.value
  homeStore.featuresDesc = featuresDesc.value
  homeStore.featuresImage = featuresImage.value
  homeStore.featuresBadgeTitle = featuresBadgeTitle.value
  homeStore.featuresBadgeDesc = featuresBadgeDesc.value
  homeStore.ctaTitle = ctaTitle.value
  homeStore.ctaDesc = ctaDesc.value
  homeStore.ctaButtonText = ctaButtonText.value
  homeStore.ctaButtonLink = ctaButtonLink.value
  homeStore.homeShowTestimonials = homeShowTestimonials.value
  homeStore.homeShowPartners = homeShowPartners.value
  homeStore.homeShowAffiliates = homeShowAffiliates.value
  homeStore.homeShowFaq = homeShowFaq.value
  homeStore.homeShowShowcase = homeShowShowcase.value
  homeStore.homeShowStats = homeShowStats.value
  homeStore.homeShowFeatures = homeShowFeatures.value
  homeStore.homeShowHowItWorks = homeShowHowItWorks.value
  homeStore.categories = categories.value
  homeStore.showcaseProducts = showcaseProducts.value
  homeStore.recentProjects = recentProjects.value
  homeStore.recentArticles = recentArticles.value
  homeStore.highlightCategoriesList = highlightCategoriesList.value
  homeStore.showcaseCategoriesData = showcaseCategoriesData.value
  homeStore.bentoCards = bentoCards.value
  homeStore.trustBadges = trustBadges.value
  homeStore.lifestyleItems = lifestyleItems.value
  homeStore.secondaryCategories = secondaryCategories.value
  homeStore.isLoaded = true
  homeStore.lastFetched = Date.now()
  homeStore.saveCache()
}

const isPageDataReady = ref(homeStore.isLoaded)

const initData = async () => {
  if (homeStore.isLoaded && (Date.now() - homeStore.lastFetched < 600000)) {
    isPageDataReady.value = true
    addStructuredData()
    addFaqSchema()
    startShowcaseProgress()
    if (slides.value.length > 1 && !slideInterval) {
      startSlideTimer()
    }
    return
  }

  await loadHomepageSettings()
  addStructuredData()
  addFaqSchema()
  await Promise.all([
    fetchShowcaseProducts(),
    fetchRecentContent(),
    fetchCategories()
  ])
  await buildShowcaseCategoriesData()
  
  startShowcaseProgress()

  syncToHomeStore()
  isPageDataReady.value = true
}

const handleVisibilityChange = () => {
  if (document.hidden) {
    stopSlideTimer()
    clearInterval(showcaseProgressInterval)
  } else {
    if (slides.value.length > 1) startSlideTimer()
    if (showcaseItemsList.value.length > 1) startShowcaseProgress()
  }
}

onMounted(async () => {
  if (slides.value.length > 1 && !slideInterval) {
    startSlideTimer()
  }
  promotionsStore.fetchActivePromotions()
  promotionsStore.fetchProductPromos()
  setTimeout(() => onShowcaseScroll(), 300)

  await initData()
  if (authStore.isAuthenticated) {
    wishlistStore.fetchWishlist()
  }
  
  // Refresh AOS after content loads
  await nextTick()

  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  if (!settingsLoading.value && showcaseCategoriesData.value.length > 0) {
    syncToHomeStore()
  }
  stopSlideTimer()
  stopShowcaseProgress()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  
  const script = document.getElementById('json-ld-localbusiness')
  if (script) document.head.removeChild(script)
  const faqScript = document.getElementById('json-ld-faq')
  if (faqScript) document.head.removeChild(faqScript)
})
</script>

<template>
  <div 
    class="home-page overflow-x-hidden bg-white dark:bg-[#0a0f16]"
    :data-page-loading="!isPageDataReady ? 'true' : 'false'"
  >
    <!-- Hero Bento & Trust Badges Hero Section (Full Viewport Height First Fold) -->
    <section class="hero-fold-section bg-[#fbfaf8] dark:bg-[#0a0f16] flex flex-col justify-between py-3 sm:py-4 lg:py-4.5">
        <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 w-full flex-1 flex flex-col justify-center min-h-0">
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 h-full flex-1 min-h-0">
            
            <!-- Left: Main Hero Slide Carousel (7 cols) -->
            <div class="lg:col-span-7 relative h-[440px] sm:h-[480px] lg:h-full min-h-[440px] sm:min-h-[480px] lg:min-h-[520px] rounded-3xl overflow-hidden shadow-lg border border-gray-200/70 dark:border-gray-800 bg-[#0e1f15] group">
              <!-- Carousel slides -->
              <transition name="fade" mode="out-in">
                <div v-if="slides[currentSlide]" :key="currentSlide" class="absolute inset-0">
                  <img
                    v-if="slides[currentSlide].image"
                    :src="getOptimizedImageUrl(slides[currentSlide].image, 1000)"
                    :srcset="`${getOptimizedImageUrl(slides[currentSlide].image, 600)} 600w, ${getOptimizedImageUrl(slides[currentSlide].image, 1000)} 1000w, ${getOptimizedImageUrl(slides[currentSlide].image, 1600)} 1600w`"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 60vw"
                    :alt="slides[currentSlide].titleLine1"
                    class="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    loading="eager"
                    fetchpriority="high"
                    width="800"
                    height="520"
                  />
                  <!-- Smooth Eased Scrim: dark only at the bottom behind text, leaving upper image bright and clear -->
                  <div class="absolute inset-x-0 bottom-0 h-[62%] sm:h-[58%] pointer-events-none scrim-bottom-text"></div>

                  <!-- Text & CTA Content -->
                  <div class="absolute inset-0 p-5 sm:p-8 lg:p-10 flex flex-col justify-between text-white z-10">
                    <!-- Top Tag -->
                    <div v-if="slides[currentSlide].tag" class="flex items-center gap-2">
                      <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0b533b]/95 text-white text-[10px] sm:text-[11px] font-black uppercase tracking-wider shadow-sm border border-emerald-400/30 backdrop-blur-sm">
                        <span class="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse"></span>
                        {{ slides[currentSlide].tag }}
                      </span>
                    </div>
                    <div v-else></div>

                    <!-- Bottom Content Area (Moved down to stick near bottom) -->
                    <div class="mt-auto space-y-2 sm:space-y-3 max-w-xl pb-1 sm:pb-2">
                      <h1 class="text-xl sm:text-3xl lg:text-4xl xl:text-[42px] font-black tracking-tight leading-snug sm:leading-[1.2] text-white drop-shadow-md">
                        {{ slides[currentSlide].titleLine1 }}
                        <br v-if="slides[currentSlide].titleLine2" />
                        <span class="text-emerald-300">
                          {{ slides[currentSlide].titleLine2 }}
                        </span>
                      </h1>

                      <p v-if="slides[currentSlide].desc" class="text-xs sm:text-sm text-gray-200 line-clamp-2 max-w-lg leading-relaxed drop-shadow">
                        {{ slides[currentSlide].desc }}
                      </p>

                      <!-- Discount & CTA Button -->
                      <div class="flex flex-wrap items-center gap-3 pt-2">
                        <div v-if="slides[currentSlide].discountBadge" class="inline-flex items-center px-3.5 py-1.5 rounded-full bg-amber-400 text-gray-950 font-black text-xs sm:text-sm shadow-md">
                          {{ slides[currentSlide].discountBadge }}
                        </div>

                        <router-link
                          v-if="slides[currentSlide].ctaText"
                          :to="slides[currentSlide].ctaLink || '/products'"
                          class="inline-flex items-center gap-2 px-6 py-2.5 sm:py-3 rounded-xl bg-[#a8381e] hover:bg-[#8f2f18] active:bg-[#7a2713] text-white font-extrabold text-xs sm:text-sm shadow-lg shadow-black/30 hover:scale-105 transition-all"
                        >
                          <span>{{ slides[currentSlide].ctaText }}</span>
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/></svg>
                        </router-link>
                      </div>
                    </div>

                    <!-- Slide Dots & Controls -->
                    <div class="flex items-center justify-between pt-2">
                      <div class="flex items-center gap-1">
                        <button
                          v-for="(s, sIdx) in slides"
                          :key="sIdx"
                          @click="goToSlide(sIdx)"
                          class="w-12 h-12 flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 cursor-pointer"
                          :aria-label="'สไลด์ ' + (sIdx + 1)"
                        >
                          <span
                            class="block h-2 rounded-full transition-all duration-300"
                            :class="currentSlide === sIdx ? 'w-6 bg-emerald-400' : 'w-2 bg-white/40 hover:bg-white/70'"
                          ></span>
                        </button>
                      </div>

                      <div v-if="slides.length > 1" class="flex items-center gap-2">
                        <button
                          @click="prevSlide"
                          aria-label="ก่อนหน้า"
                          class="w-12 h-12 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur-sm border border-white/20 transition-all hover:scale-105 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                        >
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"/></svg>
                        </button>
                        <button
                          @click="nextSlide"
                          aria-label="ถัดไป"
                          class="w-12 h-12 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur-sm border border-white/20 transition-all hover:scale-105 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                        >
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Clean Skeleton while loading -->
                <div v-else-if="!isPageDataReady" class="absolute inset-0 bg-[#0e1f15] animate-pulse flex items-center justify-center">
                  <div class="space-y-4 max-w-md w-full px-8">
                    <div class="h-6 w-32 bg-emerald-900/50 rounded-full"></div>
                    <div class="h-10 w-3/4 bg-emerald-800/40 rounded-xl"></div>
                    <div class="h-4 w-full bg-emerald-900/30 rounded"></div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Right: 2x2 Bento Cards Grid (5 cols) -->
            <div class="lg:col-span-5 grid grid-cols-2 grid-rows-2 gap-3 sm:gap-4 h-[440px] sm:h-[480px] lg:h-full min-h-[440px] sm:min-h-[480px] lg:min-h-[520px]">
              <template v-if="bentoCards && bentoCards.length > 0">
                <router-link
                  v-for="bento in bentoCards"
                  :key="bento.id || bento.title"
                  :to="bento.ctaLink || '/products'"
                  class="group relative rounded-2xl overflow-hidden shadow-md border border-gray-200/80 dark:border-gray-800 flex flex-col justify-between p-4 sm:p-5 transition-all hover:shadow-xl hover:-translate-y-0.5 bg-gray-900"
                >
                  <!-- Card Background Image -->
                  <img
                    v-if="bento.image"
                    :src="getOptimizedImageUrl(bento.image, 400)"
                    :srcset="`${getOptimizedImageUrl(bento.image, 400)} 400w, ${getOptimizedImageUrl(bento.image, 700)} 700w`"
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 50vw, 320px"
                    :alt="bento.title"
                    class="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-108 transition-transform duration-700"
                    loading="lazy"
                    decoding="async"
                    width="320"
                    height="260"
                  />

                  <!-- Smooth Eased Scrim: dark only at the bottom behind text -->
                  <div 
                    :class="[
                      bento.theme === 'light' ? 'scrim-bottom-text-light' : 'scrim-bottom-text',
                      'absolute inset-x-0 bottom-0 h-[60%] sm:h-[55%] pointer-events-none transition-opacity duration-300 group-hover:opacity-95'
                    ]"
                  ></div>

                  <!-- Card Top: Badge or Promo -->
                  <div class="relative z-10 flex items-start justify-between gap-2">
                    <span
                      v-if="bento.badge"
                      class="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-[#0b533b] text-white shadow-sm"
                    >
                      {{ bento.badge }}
                    </span>
                    <span
                      v-else-if="bento.promoText"
                      class="px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-wider bg-amber-400 text-gray-950 shadow-sm"
                    >
                      {{ bento.promoText }}
                    </span>
                    <span v-else></span>
                  </div>

                  <!-- Card Bottom: Title, Subtitle, and Chevron Button -->
                  <div class="relative z-10 flex items-end justify-between gap-2 pt-4">
                    <div class="space-y-1">
                      <h2 class="text-sm sm:text-base font-black text-white leading-tight drop-shadow-sm group-hover:text-emerald-300 transition-colors">
                        {{ bento.title }}
                      </h2>
                      <p v-if="bento.subtitle" class="text-[11px] text-gray-200 line-clamp-1 drop-shadow-sm">
                        {{ bento.subtitle }}
                      </p>
                    </div>

                    <!-- Circular Action Chevron -->
                    <div class="w-7 h-7 rounded-full bg-white/90 text-gray-900 group-hover:bg-emerald-500 group-hover:text-white flex items-center justify-center shrink-0 shadow-sm transition-all group-hover:scale-110">
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/></svg>
                    </div>
                  </div>
                </router-link>
              </template>
              <template v-else-if="!isPageDataReady">
                <div v-for="i in 4" :key="i" class="rounded-2xl bg-gray-200/60 dark:bg-gray-800/60 animate-pulse"></div>
              </template>
            </div>

          </div>
        </div>

        </section>

    <!-- Active Promotions Section (Below Hero Slider) -->
    <section v-if="promotionsStore.hasActivePromotions" class="py-12 md:py-16 bg-slate-50/80 dark:bg-[#0b0f19] border-y border-gray-100 dark:border-gray-800/60 relative">
      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10">
        
        <!-- Section Header -->
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/80 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-200 text-xs font-bold border border-emerald-300/60 dark:border-emerald-700/50 mb-2">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <span>SPECIAL PROMOTIONS & OFFERS</span>
            </div>
            <h2 class="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-3">
              <span>โปรโมชั่นและสิทธิพิเศษ</span>
              <span class="text-xs px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 font-bold border border-emerald-500/30">
                {{ promotionsStore.activePromotions.length }} แคมเปญ
              </span>
            </h2>
          </div>

          <router-link to="/promotions" class="inline-flex items-center gap-2 text-xs font-bold text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-200 transition-colors group">
            <span>ดูรายการโปรโมชั่นทั้งหมด</span>
            <svg class="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </router-link>
        </div>

        <!-- Active Promotions List / Cards -->
        <div class="space-y-8">
          <div v-for="promo in promotionsStore.activePromotions" :key="promo.id" class="bg-white dark:bg-[#111827] rounded-3xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-800 hover:border-emerald-500/40 hover:shadow-xl transition-all duration-300 group">
            
            <!-- Top Campaign Banner & Details Row -->
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch bg-gradient-to-br from-[#062419] via-[#0b4631] to-[#081c15]">
              
              <!-- Left: Standalone Crisp Banner Image (Strict 16:9 Aspect Ratio) -->
              <div v-if="promo.banner_image" class="lg:col-span-6 relative overflow-hidden bg-slate-900 group/banner cursor-pointer aspect-[16/9]">
                <router-link to="/promotions" class="block w-full h-full relative">
                  <img :src="getOptimizedImageUrl(promo.banner_image, 700)" :alt="promo.title || 'รูปภาพโปรโมชั่น'" class="w-full h-full object-cover transition-transform duration-500 group-hover/banner:scale-[1.02]" loading="lazy" decoding="async" width="600" height="338">
                  <div class="absolute inset-0 bg-black/0 group-hover/banner:bg-black/10 transition-colors"></div>
                </router-link>
              </div>

              <!-- Right (or Full): Campaign Info & Action Header -->
              <div :class="promo.banner_image ? 'lg:col-span-6' : 'lg:col-span-12'" class="p-6 md:p-8 flex flex-col justify-between relative overflow-hidden">
                <!-- Badges -->
                <div class="relative z-10 flex flex-wrap items-center gap-2">
                  <span v-if="promo.fulfillment_method === 'STORE_PICKUP'" class="px-3 py-1 rounded-full text-xs font-bold bg-amber-500 text-slate-950 flex items-center gap-1.5 shadow-sm">
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
                    รับสินค้าหน้าร้าน
                  </span>
                  <span v-else-if="promo.fulfillment_method === 'DELIVERY'" class="px-3 py-1 rounded-full text-xs font-bold bg-sky-700 text-white flex items-center gap-1.5 shadow-sm">
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"/></svg>
                    จัดส่งตามที่อยู่
                  </span>
                  <span v-else class="px-3 py-1 rounded-full text-xs font-bold bg-[#0b533b] text-white flex items-center gap-1.5 shadow-sm">
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>
                    ทุกรูปแบบการจัดส่ง
                  </span>

                  <span class="px-3 py-1 rounded-full text-xs font-black bg-[#dc2626] text-white shadow-sm">
                    {{ getPromoDiscountBadgeText(promo) }}
                  </span>
                </div>

                <!-- Promo Content -->
                <div class="relative z-10 space-y-2 my-auto py-4">
                  <h3 class="text-xl md:text-2xl font-black text-white leading-tight group-hover:text-emerald-300 transition-colors">
                    {{ promo.title }}
                  </h3>
                  <p class="text-xs md:text-sm text-emerald-100/85 line-clamp-2 font-normal leading-relaxed">
                    {{ promo.description || 'สิทธิพิเศษและส่วนลดราคาเฉพาะสั่งซื้อตามเงื่อนไขแคมเปญ' }}
                  </p>
                </div>

                <!-- Action Button -->
                <div class="relative z-10 pt-2 flex flex-wrap items-center justify-between gap-3 border-t border-white/10">
                  <span class="text-xs font-mono text-emerald-300 font-bold">
                    {{ promo.showcase_products?.length ? `สินค้าเข้าร่วม ${promo.showcase_products.length} รายการ` : 'สินค้าเข้าร่วมโปรโมชั่น' }}
                  </span>
                  <router-link to="/promotions" class="px-4 py-2 bg-white hover:bg-emerald-50 text-[#062419] font-extrabold rounded-xl text-xs transition-all flex items-center gap-1.5 shadow-sm">
                    <span>รายละเอียดโปรโมชั่น</span>
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                  </router-link>
                </div>
              </div>

            </div>

            <!-- Bottom: Participating Products Showcase Grid (ProductCard) -->
            <div class="p-5 sm:p-6 lg:p-8 bg-white dark:bg-[#111827] border-t border-gray-100 dark:border-gray-800">
              <div class="flex items-center justify-between pb-4 mb-6 border-b border-gray-100 dark:border-gray-800/80">
                <span class="text-sm sm:text-base font-black text-gray-900 dark:text-white flex items-center gap-2">
                  <svg class="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>
                  รายการสินค้าที่เข้าร่วมโปรโมชั่นนี้
                </span>

                <router-link to="/promotions" class="px-3.5 py-1.5 bg-emerald-100/80 hover:bg-emerald-200/80 dark:bg-emerald-900/50 dark:hover:bg-emerald-900/80 text-emerald-800 dark:text-emerald-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1">
                  <span>ดูสินค้าโปรโมชั่นทั้งหมด</span>
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                </router-link>
              </div>

              <div v-if="promo.showcase_products && promo.showcase_products.length > 0" :class="settingsStore.productGridClass">
                <ProductCard
                  v-for="item in promo.showcase_products.slice(0, 3)"
                  :key="item.id"
                  :product="{
                    ...item,
                    price: getItemPromoPrice(item, promo),
                    promo_price: getItemPromoPrice(item, promo),
                    original_price: getItemOriginalPrice(item)
                  }"
                />
              </div>

              <!-- Fallback if no specific products attached -->
              <div v-else class="py-8 text-center bg-gray-50/50 dark:bg-gray-800/30 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800 text-xs text-gray-500">
                สิทธิพิเศษใช้ได้กับสินค้าทุกรายการในร้าน เมื่อสั่งซื้อตรงตามเงื่อนไข
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>




    
    <!-- Category Showcase Blocks -->
    <template v-if="homeShowShowcase && showcaseCategoriesData.length > 0">
      <section v-for="(showcase, index) in showcaseCategoriesData" :key="index" class="py-20 md:py-28 bg-white dark:bg-[#0a0f16] border-t border-gray-100 dark:border-gray-800/50">
        <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10">
          <!-- Modern Architectural Category Showcase Header Banner (Clean White Luxury Theme) -->
          <div class="relative overflow-hidden rounded-[28px] bg-white dark:bg-slate-900 p-5 sm:p-6 lg:px-8 lg:py-6 mb-10 md:mb-12 shadow-[0_10px_35px_rgba(0,0,0,0.05)] dark:shadow-[0_14px_40px_rgba(0,0,0,0.35)] border border-slate-200/90 dark:border-slate-800">
            <!-- Decorative Subtle Ambient Glow & Watermark Number -->
            <div class="absolute -right-12 -top-16 w-64 h-64 rounded-full bg-emerald-500/5 dark:bg-emerald-400/10 blur-3xl pointer-events-none"></div>
            <div class="absolute -left-12 -bottom-16 w-56 h-56 rounded-full bg-teal-500/5 dark:bg-teal-300/10 blur-3xl pointer-events-none"></div>
            <span class="hidden md:block absolute right-56 -bottom-4 text-[110px] font-black text-slate-900/[0.03] dark:text-white/[0.04] leading-none tracking-tighter select-none pointer-events-none">
              {{ getCategoryShowcaseMeta(showcase.category, index).indexLabel }}
            </span>

            <div class="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-5">
              <!-- Left: Framed Glass Emblem + Title + Feature Pills -->
              <div class="flex items-start sm:items-center gap-4 sm:gap-5 min-w-0">
                <!-- Layered Icon Pod with Index Badge -->
                <div class="relative shrink-0">
                  <span class="absolute -top-2 -left-2 z-20 px-2 py-0.5 rounded-full bg-[#0b533b] dark:bg-emerald-500 text-white dark:text-slate-950 text-[10px] font-black tracking-wider shadow-sm">
                    #{{ getCategoryShowcaseMeta(showcase.category, index).indexLabel }}
                  </span>
                  <div v-if="showcase.category.icon_url || showcase.category.image_url" 
                       class="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-2xl overflow-hidden bg-emerald-50/60 dark:bg-slate-800 border border-emerald-100/80 dark:border-slate-700/80 flex items-center justify-center shadow-sm"
                       :class="showcase.category.icon_url ? 'p-3' : 'p-0'"
                  >
                    <img width="64" height="64" v-if="showcase.category.icon_url" :src="getOptimizedImageUrl(showcase.category.icon_url, 80)" :alt="showcase.category.name" class="w-full h-full object-contain" loading="lazy" decoding="async">
                    <img width="64" height="64" v-else :src="getOptimizedImageUrl(showcase.category.image_url, 80)" :alt="showcase.category.name" class="w-full h-full object-cover" loading="lazy" decoding="async">
                  </div>
                  <div v-else class="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-2xl bg-emerald-50/60 dark:bg-slate-800 border border-emerald-100/80 dark:border-slate-700/80 flex items-center justify-center shadow-sm">
                    <svg class="w-8 h-8 text-[#0b533b] dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>
                  </div>
                </div>

                <!-- Category Identity & Feature Pills -->
                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-center gap-2 mb-1.5">
                    <span class="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200/80 dark:border-emerald-800/80 text-[#0b533b] dark:text-emerald-300 text-[11px] font-extrabold tracking-wide">
                      <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      หมวดหมู่สินค้าแนะนำ
                    </span>
                    <span 
                      v-for="(badgeText, bIdx) in getCategoryShowcaseMeta(showcase.category, index).badges" 
                      :key="bIdx"
                      class="hidden sm:inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200/70 dark:border-slate-700/70 text-slate-700 dark:text-slate-300 text-[10.5px] font-semibold"
                    >
                      <svg class="w-3 h-3 text-emerald-600 dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                      </svg>
                      {{ badgeText }}
                    </span>
                  </div>

                  <h2 class="text-2xl sm:text-3xl lg:text-[32px] font-black text-slate-900 dark:text-white tracking-tight leading-tight truncate">
                    {{ showcase.category.name }}
                  </h2>

                  <p class="text-xs sm:text-[13.5px] text-slate-500 dark:text-slate-400 font-medium mt-1 line-clamp-1">
                    {{ getCategoryShowcaseMeta(showcase.category, index).subtitle }}
                  </p>
                </div>
              </div>

              <!-- Right: Interactive Action Button with Circular Arrow Pod -->
              <div class="flex items-center justify-between lg:justify-end gap-4 pt-3 lg:pt-0 border-t border-slate-100 dark:border-slate-800 lg:border-t-0 shrink-0">
                <div class="hidden xl:flex flex-col items-end text-right pr-2 border-r border-slate-200 dark:border-slate-700">
                  <span class="text-[11px] font-bold text-[#0b533b] dark:text-emerald-400 uppercase tracking-wider">คัดสรรรุ่นยอดนิยม</span>
                  <span class="text-xs font-medium text-slate-500 dark:text-slate-400">พร้อมจัดส่งทั่วประเทศ</span>
                </div>

                <router-link 
                  :to="`/products/category/${encodeURIComponent(showcase.category.slug || showcase.category.name)}`" 
                  :aria-label="'ดูสินค้าทั้งหมดในหมวดหมู่ ' + showcase.category.name" 
                  class="group/catbtn inline-flex items-center justify-between sm:justify-start gap-3 w-full sm:w-auto pl-5 pr-2 py-2 rounded-full bg-[#0b533b] hover:bg-[#115e42] text-white text-xs sm:text-sm font-extrabold shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap hover:-translate-y-0.5"
                >
                  <span>ดูทั้งหมดในหมวดหมู่นี้</span>
                  <span class="w-8 h-8 rounded-full bg-white/20 group-hover/catbtn:bg-white text-white group-hover/catbtn:text-[#0b533b] flex items-center justify-center transition-all duration-300 group-hover/catbtn:translate-x-0.5 shrink-0">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                    </svg>
                  </span>
                </router-link>
              </div>
            </div>
          </div>

          <div :class="settingsStore.productGridClass">
            <ProductCard 
              v-for="(product, pIdx) in showcase.products" 
              :key="product.id"
              :product="product"
              :priority="index === 0 && pIdx < 4"
            />
          </div>


        </div>
      </section>
    </template>

    <!-- Brand Value Proposition & Features Story -->
    <section v-if="homeShowFeatures && (featuresItems.length > 0 || featuresTitle)" class="py-20 md:py-28 bg-[#f8faf9] dark:bg-[#0d1522] border-t border-slate-200/70 dark:border-white/5 relative overflow-hidden">
      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          
          <!-- Left 7 Cols: Narrative & 4 Feature Pillars -->
          <div class="lg:col-span-7 space-y-6">
            <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-800 dark:text-emerald-300 text-xs font-black tracking-wider uppercase">
              <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span>{{ featuresHeading || 'ความลงตัวของทุกพื้นที่' }}</span>
            </div>

            <h2 class="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 dark:text-white leading-[1.18]" v-html="featuresTitle || 'Outdoor Lander: นวัตกรรมพื้นที่ส่วนตัว'"></h2>

            <p class="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl" v-html="featuresDesc"></p>

            <!-- 4 Value Proposition Cards -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              <div 
                v-for="(feat, fIdx) in featuresItems" 
                :key="fIdx"
                class="p-4 rounded-2xl bg-white dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/80 shadow-xs hover:shadow-md transition-all duration-300"
              >
                <div class="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 flex items-center justify-center mb-3">
                  <FeatureIcon :name="feat.icon || 'shield'" class="w-5 h-5" />
                </div>
                <h4 class="text-sm font-black text-slate-900 dark:text-white mb-1">{{ feat.title }}</h4>
                <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{{ feat.desc }}</p>
              </div>
            </div>
          </div>

          <!-- Right 5 Cols: Showcase Image & Floating Badge -->
          <div class="lg:col-span-5 relative">
            <div class="relative rounded-3xl overflow-hidden aspect-[4/3] sm:aspect-[16/11] bg-slate-900 shadow-2xl border border-slate-200/80 dark:border-slate-700/80 group">
              <img 
                v-if="featuresImage"
                :src="getOptimizedImageUrl(featuresImage, 1000)" 
                alt="Brand Features" 
                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div v-else class="w-full h-full bg-slate-900 flex items-center justify-center"></div>
              <div class="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent"></div>

              <!-- Floating Glass Badge -->
              <div v-if="featuresBadgeTitle || featuresBadgeDesc" class="absolute bottom-5 left-5 right-5 p-4 rounded-2xl bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border border-white/40 dark:border-slate-700/80 shadow-lg">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-xl bg-[#0b533b] text-white flex items-center justify-center font-black text-sm shrink-0 shadow-xs">
                    OL
                  </div>
                  <div class="min-w-0">
                    <h5 class="text-xs font-black text-slate-900 dark:text-white truncate">{{ featuresBadgeTitle || 'Premium Quality' }}</h5>
                    <p class="text-[11px] text-slate-600 dark:text-slate-300 truncate">{{ featuresBadgeDesc || 'มาตรฐานวิศวกรรมระดับสากล' }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- How It Works / 4-Step Service Process -->
    <section v-if="homeShowHowItWorks && howItWorksSteps.length > 0" class="py-20 md:py-28 bg-white dark:bg-[#0a0f16] border-t border-slate-200/70 dark:border-white/5 relative overflow-hidden">
      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
        
        <div class="text-center max-w-2xl mx-auto mb-14">
          <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-800 dark:text-emerald-300 text-xs font-black tracking-wider uppercase mb-3.5">
            <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>{{ sectionTitles.howItWorksTitle || 'ขั้นตอนการบริการ' }}</span>
          </div>
          <h2 class="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 dark:text-white leading-[1.18]">
            {{ sectionTitles.howItWorksHeading || 'ง่ายแค่ 4 ขั้นตอน พร้อมใช้งานทันที' }}
          </h2>
          <p class="text-sm sm:text-base text-slate-600 dark:text-slate-400 mt-2.5 leading-relaxed">
            {{ sectionTitles.howItWorksDesc || 'จากปรึกษาตัวเองถึงติดตั้งเสร็จสมบูรณ์ เราดูแลทุกขั้นตอนอย่างมืออาชีพ' }}
          </p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          <div 
            v-for="(step, sIdx) in howItWorksSteps" 
            :key="sIdx"
            class="group relative p-6 rounded-3xl bg-[#f8faf9] dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 hover:border-emerald-500/50 hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
          >
            <div>
              <div class="flex items-center justify-between mb-5">
                <span class="text-2xl font-black font-mono text-emerald-600 dark:text-emerald-400">
                  {{ step.step || String(sIdx + 1).padStart(2, '0') }}
                </span>
                <div class="w-10 h-10 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shadow-2xs group-hover:scale-110 transition-transform">
                  <FeatureIcon :name="step.icon || 'shield'" class="w-5 h-5" />
                </div>
              </div>
              <h3 class="text-base font-black text-slate-900 dark:text-white mb-2 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                {{ step.title }}
              </h3>
              <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                {{ step.desc }}
              </p>
            </div>

            <div class="pt-5 mt-5 border-t border-slate-200/60 dark:border-slate-800 flex items-center justify-between text-[11px] font-bold text-slate-600 dark:text-slate-400">
              <span>STEP {{ sIdx + 1 }}</span>
              <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            </div>
          </div>
        </div>

      </div>
    </section>

    <!-- Milestone Statistics Counters -->
    <section v-if="homeShowStats && stats.length > 0" class="py-14 md:py-20 bg-gradient-to-r from-emerald-950 via-slate-900 to-[#072418] text-white relative overflow-hidden border-t border-emerald-900/40">
      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          <div 
            v-for="(st, stIdx) in stats" 
            :key="stIdx"
            class="p-4 sm:p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm text-center sm:text-left flex flex-col justify-between"
          >
            <div>
              <div class="text-3xl sm:text-4xl lg:text-5xl font-black text-emerald-400 tracking-tight font-mono mb-2">
                {{ st.number }}
              </div>
              <h4 class="text-sm sm:text-base font-black text-white mb-1">
                {{ st.label }}
              </h4>
              <p class="text-xs text-slate-200 leading-relaxed">
                {{ st.desc }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Recent Projects Showcase (Modern Interactive Architectural Portfolio) -->
    <section v-if="recentProjects.length > 0" class="py-20 md:py-28 bg-gradient-to-b from-[#f4f7f5] via-white to-[#f8faf9] dark:from-[#0b111e] dark:via-[#0f172a] dark:to-[#0b111e] relative overflow-hidden border-t border-slate-200/70 dark:border-slate-800/70">
      <!-- Ambient Architectural Accents -->
      <div class="absolute inset-0 pointer-events-none overflow-hidden">
        <div class="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-emerald-500/8 blur-[110px]"></div>
        <div class="absolute bottom-0 right-0 w-[28rem] h-[28rem] rounded-full bg-teal-500/8 blur-[120px]"></div>
      </div>

      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
        
        <!-- 1. Editorial Header + Live Portfolio Metrics -->
        <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 md:mb-14">
          <div class="max-w-2xl">
            <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0b533b]/10 dark:bg-emerald-500/15 border border-[#0b533b]/20 dark:border-emerald-500/30 text-[#0b533b] dark:text-emerald-300 text-xs font-extrabold tracking-wider uppercase mb-3.5">
              <span class="w-2 h-2 rounded-full bg-[#0b533b] dark:bg-emerald-400 animate-pulse"></span>
              <span>{{ sectionTitles.projectsTitle || 'ผลงานของเรา' }} • REAL INSTALLATION</span>
            </div>
            <h2 class="text-3xl sm:text-4xl lg:text-[42px] font-black tracking-tight text-slate-900 dark:text-white leading-[1.15]">
              {{ sectionTitles.projectsHeading }}
            </h2>
            <p class="text-sm sm:text-base text-slate-600 dark:text-slate-400 mt-2.5 leading-relaxed">
              พิสูจน์คุณภาพโครงสร้างและงานติดตั้งมาตรฐานวิศวกรรมจากหน้างานจริงทั่วประเทศ โดยทีมช่างเฉพาะทาง
            </p>
          </div>

          <!-- Right: Trust Counters & View All Pill Button -->
          <div class="flex flex-wrap items-center gap-3 sm:gap-4 shrink-0">
            <div class="hidden sm:flex items-center gap-5 px-5 py-2.5 rounded-2xl bg-white dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700/80 shadow-2xs">
              <div class="text-left">
                <div class="text-lg font-black text-[#0b533b] dark:text-emerald-400 leading-none">500+</div>
                <div class="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mt-0.5">ส่งมอบงานติดตั้ง</div>
              </div>
              <div class="w-px h-7 bg-slate-200 dark:bg-slate-700"></div>
              <div class="text-left">
                <div class="text-lg font-black text-[#0b533b] dark:text-emerald-400 leading-none">77 จังหวัด</div>
                <div class="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mt-0.5">บริการทั่วประเทศ</div>
              </div>
            </div>

            <router-link 
              to="/projects" 
              class="group/allbtn inline-flex items-center gap-3 pl-5 pr-2 py-2 rounded-full bg-[#0b533b] hover:bg-[#0d684a] text-white text-xs sm:text-sm font-extrabold shadow-lg shadow-emerald-950/15 transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap"
            >
              <span>ดูผลงานทั้งหมด</span>
              <span class="w-8 h-8 rounded-full bg-white/15 group-hover/allbtn:bg-white group-hover/allbtn:text-[#0b533b] flex items-center justify-center transition-all duration-300">
                <svg class="w-4 h-4 transform group-hover/allbtn:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                </svg>
              </span>
            </router-link>
          </div>
        </div>

        <!-- 2. Interactive Split Portfolio Stage (Left: Featured 1:1 Spotlight | Right: Selector Stack + Service CTA) -->
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-7 items-start">
          
          <!-- LEFT (6 Cols): Featured Installation Spotlight Card (1:1 Cover Image) -->
          <div v-if="featuredProject" class="lg:col-span-6 flex flex-col">
            <div class="group rounded-[28px] overflow-hidden bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-[0_20px_50px_rgba(15,23,42,0.08)] flex flex-col">
              
              <!-- 1:1 Square Cover Image Container -->
              <div class="relative aspect-square w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
                <router-link :to="'/projects/' + featuredProject.slug" class="block w-full h-full">
                  <img 
                    :key="featuredProject.id"
                    :src="getOptimizedImageUrl(featuredProject.cover_image, 1000)" 
                    :alt="featuredProject.title" 
                    loading="lazy"
                    decoding="async"
                    class="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-105"
                  >
                </router-link>

                <!-- Top Floating Bar on Image -->
                <div class="absolute top-4 left-4 right-4 z-[2] flex flex-wrap items-center justify-between gap-2 pointer-events-none">
                  <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0b533b]/95 text-white text-xs font-black tracking-wide shadow-md border border-white/20">
                    <span class="px-1.5 py-0.5 rounded-full bg-emerald-400 text-[#072e20] text-[10px] font-black leading-none">
                      #{{ String(activeProjectIndex + 1).padStart(2, '0') }}
                    </span>
                    <span>ผลงานติดตั้งจริง</span>
                  </div>

                  <div class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/95 dark:bg-slate-900/95 text-slate-800 dark:text-slate-100 text-xs font-bold shadow-md border border-white/80 dark:border-slate-700">
                    <svg class="w-3.5 h-3.5 text-[#0b533b] dark:text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    <span>{{ featuredProject.location || 'ประเทศไทย' }}</span>
                  </div>
                </div>
              </div>

              <!-- Info Card Below 1:1 Cover Image -->
              <div class="p-5 sm:p-6 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-center gap-3 text-xs font-bold text-[#0b533b] dark:text-emerald-400 mb-1.5">
                    <span class="inline-flex items-center gap-1">
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      ส่งมอบงานเรียบร้อย
                    </span>
                    <span v-if="featuredProject.service_date" class="inline-flex items-center gap-1 text-slate-500 dark:text-slate-400 font-semibold">
                      <span>•</span>
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      {{ new Date(featuredProject.service_date).toLocaleDateString('th-TH', { year: 'numeric', month: 'short' }) }}
                    </span>
                  </div>

                  <router-link :to="'/projects/' + featuredProject.slug" class="block group/title">
                    <h3 class="text-lg sm:text-xl lg:text-[22px] font-black text-slate-900 dark:text-white leading-snug group-hover/title:text-[#0b533b] dark:group-hover/title:text-emerald-400 transition-colors">
                      {{ featuredProject.title }}
                    </h3>
                  </router-link>
                </div>

                <router-link 
                  :to="'/projects/' + featuredProject.slug"
                  class="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#0b533b] hover:bg-[#0d684a] text-white text-xs sm:text-sm font-extrabold shadow-md transition-all duration-200 hover:shadow-lg group/btn whitespace-nowrap"
                >
                  <span>ดูรายละเอียด</span>
                  <svg class="w-4 h-4 transform group-hover/btn:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                  </svg>
                </router-link>
              </div>

            </div>
          </div>

          <!-- RIGHT (6 Cols): Interactive 4-Project Selector List + Nationwide Installation Banner -->
          <div class="lg:col-span-6 flex flex-col justify-between gap-3.5">
            
            <!-- 4 Interactive Project Cards -->
            <div class="grid grid-cols-1 gap-3">
              <router-link
                v-for="(project, idx) in recentProjects"
                :key="project.id"
                :to="'/projects/' + project.slug"
                @mouseenter="activeProjectIndex = idx"
                class="group/card relative rounded-2xl p-3 sm:p-3.5 transition-all duration-300 flex items-center gap-3.5 sm:gap-4 border"
                :class="activeProjectIndex === idx
                  ? 'bg-white dark:bg-slate-800 border-[#0b533b] dark:border-emerald-500 shadow-[0_12px_30px_rgba(11,83,59,0.12)] -translate-y-0.5'
                  : 'bg-white/80 dark:bg-slate-900/80 hover:bg-white dark:hover:bg-slate-800 border-slate-200/80 dark:border-slate-800 shadow-2xs hover:shadow-md'"
              >
                <!-- Left Active Accent Bar -->
                <span 
                  class="w-1 self-stretch rounded-full transition-colors shrink-0"
                  :class="activeProjectIndex === idx ? 'bg-[#0b533b] dark:bg-emerald-400' : 'bg-transparent group-hover/card:bg-slate-200 dark:group-hover/card:bg-slate-700'"
                ></span>

                <!-- 1:1 Square Thumbnail -->
                <div class="w-20 h-20 sm:w-[84px] sm:h-[84px] aspect-square rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0 relative border border-slate-200/60 dark:border-slate-700">
                  <img 
                    :src="getOptimizedImageUrl(project.cover_image, 300)" 
                    :alt="project.title" 
                    loading="lazy"
                    decoding="async"
                    width="160"
                    height="160"
                    class="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-110"
                  >
                  <span class="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md bg-black/70 backdrop-blur-xs text-white text-[10px] font-black leading-none">
                    #{{ String(idx + 1).padStart(2, '0') }}
                  </span>
                </div>

                <!-- Project Details -->
                <div class="min-w-0 flex-1 py-0.5">
                  <div class="flex flex-wrap items-center gap-2 text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">
                    <span class="inline-flex items-center gap-1 text-[#0b533b] dark:text-emerald-400 truncate max-w-[170px] sm:max-w-[200px]">
                      <svg class="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                      <span class="truncate">{{ project.location || 'ประเทศไทย' }}</span>
                    </span>
                    <span v-if="project.service_date" class="inline-flex items-center gap-1 shrink-0">
                      <span>•</span>
                      <span>{{ new Date(project.service_date).toLocaleDateString('th-TH', { year: 'numeric', month: 'short' }) }}</span>
                    </span>
                  </div>

                  <h4 class="text-sm sm:text-[15px] font-black text-slate-900 dark:text-white leading-snug line-clamp-2 group-hover/card:text-[#0b533b] dark:group-hover/card:text-emerald-400 transition-colors">
                    {{ project.title }}
                  </h4>
                </div>

                <!-- Right Arrow Circle -->
                <div 
                  class="w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 mr-1"
                  :class="activeProjectIndex === idx
                    ? 'bg-[#0b533b] text-white shadow-sm'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-400 group-hover/card:bg-[#0b533b] group-hover/card:text-white'"
                >
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                  </svg>
                </div>
              </router-link>
            </div>

            <!-- Bottom: Compact Architectural Service & Quotation Banner -->
            <router-link 
              to="/quotation" 
              class="group/quote relative rounded-2xl overflow-hidden bg-gradient-to-r from-[#072e20] via-[#0b533b] to-[#115e42] p-4 sm:p-5 border border-emerald-800/40 shadow-lg flex items-center justify-between gap-4 transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
            >
              <div class="absolute -right-8 -top-8 w-32 h-32 bg-emerald-400/15 rounded-full blur-2xl pointer-events-none"></div>
              <div class="flex items-center gap-3.5 min-w-0 relative z-10">
                <div class="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center text-emerald-300 shrink-0">
                  <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                </div>
                <div class="min-w-0">
                  <h4 class="text-sm sm:text-base font-black text-white truncate">บริการติดตั้งทั่วประเทศโดยทีมช่างมืออาชีพ</h4>
                  <p class="text-xs text-emerald-100/80 truncate mt-0.5">ประเมินหน้างาน รวดเร็ว ปลอดภัย ได้มาตรฐานสากล</p>
                </div>
              </div>

              <span class="relative z-10 shrink-0 px-3.5 py-2 rounded-xl bg-white text-[#0b533b] text-xs font-extrabold flex items-center gap-1.5 shadow-sm group-hover/quote:bg-emerald-50 transition-colors whitespace-nowrap">
                <span>ขอใบเสนอราคา</span>
                <svg class="w-3.5 h-3.5 transform group-hover/quote:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                </svg>
              </span>
            </router-link>

          </div>

        </div>
      </div>
    </section>



    <!-- Customer Testimonials & Reviews -->
    <section v-if="homeShowTestimonials && testimonials.length > 0" class="py-20 md:py-28 bg-[#fbfaf8] dark:bg-[#0a0f16] border-t border-slate-200/70 dark:border-white/5 relative overflow-hidden">
      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
        
        <div class="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div class="max-w-2xl">
            <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-800 dark:text-emerald-300 text-xs font-black tracking-wider uppercase mb-3.5">
              <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span>{{ sectionTitles.testimonialsTitle || 'เสียงตอบรับจากลูกค้า' }}</span>
            </div>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 dark:text-white leading-[1.18]">
              {{ sectionTitles.testimonialsHeading || 'ความไว้วางใจจากตัวจริง' }}
            </h2>
          </div>
          <div class="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
            <span>รีวิวความพึงพอใจ 5 ดาวเต็ม จากลูกค้าตัวจริง</span>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            v-for="(t, tIdx) in testimonials" 
            :key="tIdx"
            class="p-6 sm:p-7 rounded-3xl bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
          >
            <div>
              <!-- 5 Stars -->
              <div class="flex items-center gap-1 mb-4 text-amber-400">
                <svg v-for="star in (t.rating || t.stars || 5)" :key="star" class="w-4 h-4 fill-current" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
              </div>

              <!-- Comment -->
              <p class="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed mb-6 italic">
                "{{ t.comment || t.text }}"
              </p>
            </div>

            <!-- Author info -->
            <div class="flex items-center gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
              <img 
                v-if="t.avatar"
                :src="getOptimizedImageUrl(t.avatar, 100)" 
                :alt="t.name" 
                class="w-10 h-10 rounded-full object-cover border border-slate-200 dark:border-slate-700 shrink-0" 
              />
              <div v-else class="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 flex items-center justify-center font-bold text-emerald-800 dark:text-emerald-200 text-xs shrink-0">
                {{ (t.name || 'U').charAt(0).toUpperCase() }}
              </div>
              <div class="min-w-0">
                <h4 class="text-xs font-black text-slate-900 dark:text-white truncate">{{ t.name }}</h4>
                <p class="text-[11px] text-slate-600 dark:text-slate-400 truncate">{{ t.role }}</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>

    <!-- Partners & Trusted Organizations -->
    <section v-if="homeShowPartners && partners.length > 0" class="py-14 md:py-20 bg-white dark:bg-[#0d1522] border-t border-slate-200/70 dark:border-white/5 relative overflow-hidden">
      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10">
        <div class="text-center mb-8">
          <p class="text-xs font-extrabold text-slate-600 dark:text-slate-400 uppercase tracking-widest">
            {{ sectionTitles.partnersHeading || 'ความไว้วางใจจากองค์กรชั้นนำ' }}
          </p>
        </div>

        <div class="flex flex-wrap items-center justify-center gap-8 sm:gap-12 md:gap-16 opacity-75 hover:opacity-100 transition-opacity">
          <a 
            v-for="(p, pIdx) in partners" 
            :key="pIdx"
            :href="p.link || '#'" 
            target="_blank" 
            rel="noopener noreferrer"
            class="flex items-center gap-2 group p-2 rounded-xl hover:scale-105 transition-transform"
            :title="p.name"
          >
            <img v-if="p.logo || p.logo_url" :src="p.logo || p.logo_url" :alt="p.name" class="h-8 sm:h-10 w-auto object-contain grayscale group-hover:grayscale-0 transition-all duration-300" />
            <span class="text-xs sm:text-sm font-black text-slate-600 dark:text-slate-300 group-hover:text-emerald-600 transition-colors">{{ p.name }}</span>
          </a>
        </div>
      </div>
    </section>

    <!-- Latest Articles — Modern Editorial Magazine & Knowledge Hub -->
    <section v-if="recentArticles.length > 0" class="py-24 md:py-32 bg-gradient-to-b from-[#f8fafc] via-white to-[#f1f5f9] dark:from-[#0b111e] dark:via-[#0f172a] dark:to-[#0a0f18] border-t border-gray-200/70 dark:border-white/5 relative overflow-hidden">
      <!-- Ambient Editorial Background Decor -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <div class="absolute -top-24 right-[-5%] w-[480px] h-[480px] bg-emerald-500/[0.06] dark:bg-emerald-500/[0.08] blur-[130px] rounded-full"></div>
        <div class="absolute bottom-[-10%] left-[-5%] w-[420px] h-[420px] bg-teal-500/[0.05] dark:bg-teal-500/[0.07] blur-[120px] rounded-full"></div>
      </div>

      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
        <!-- Editorial Masthead Header -->
        <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-8 pb-10 mb-12 border-b border-gray-200/80 dark:border-white/10">
          <div class="max-w-2xl">
            <div class="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-emerald-950 text-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-300 text-xs font-bold tracking-widest uppercase mb-4 shadow-sm border border-emerald-800/40 dark:border-emerald-500/30">
              <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              {{ sectionTitles.articlesTitle }}
              <span class="text-emerald-500/60">|</span>
              <span class="text-[10px] text-emerald-400/90 font-semibold tracking-wider">KNOWLEDGE HUB</span>
            </div>
            <h2 class="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.15]">
              {{ sectionTitles.articlesHeading }}
            </h2>
            <p class="mt-3 text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
              เจาะลึกคู่มือการเลือกโรงเรือน เทคนิคการดูแลสวน และไอเดียจัดสรรพื้นที่กลางแจ้งจากทีมผู้เชี่ยวชาญ Outdoor Lander
            </p>
          </div>

          <!-- Right Quick Resource Links & View All CTA -->
          <div class="flex flex-wrap items-center gap-3">
            <router-link
              to="/manual"
              class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-white/5 text-xs sm:text-sm font-bold text-gray-700 dark:text-gray-200 border border-gray-200/80 dark:border-white/10 hover:border-emerald-500/50 hover:text-emerald-700 dark:hover:text-emerald-400 transition-all shadow-sm"
            >
              <svg class="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              คู่มือการติดตั้ง
            </router-link>

            <router-link
              to="/blog"
              class="group inline-flex items-center gap-3 px-6 py-3 bg-gray-900 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500 text-sm font-bold text-white rounded-xl shadow-md hover:shadow-xl hover:shadow-emerald-700/20 transition-all duration-300 hover:-translate-y-0.5"
            >
              <span>อ่านบทความทั้งหมด</span>
              <span class="w-6 h-6 rounded-lg bg-white/15 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </router-link>
          </div>
        </div>

        <!-- 1. Featured Cover Story (First Article — Full-Width Editorial Split) -->
        <router-link
          v-if="recentArticles[0]"
          :to="'/blog/' + recentArticles[0].slug"
          class="group block bg-white dark:bg-[#151f30] rounded-[2rem] p-4 sm:p-6 lg:p-8 shadow-[0_12px_45px_rgb(0,0,0,0.05)] dark:shadow-[0_12px_45px_rgb(0,0,0,0.3)] hover:shadow-[0_20px_60px_rgb(16,185,129,0.14)] border border-gray-200/80 dark:border-white/10 hover:border-emerald-500/40 transition-all duration-500 mb-8"
        >
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
            <!-- Left: 1:1 Uncropped Square Artwork Frame -->
            <div class="lg:col-span-5">
              <div class="aspect-square w-full max-w-[460px] mx-auto rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 relative shadow-md border border-gray-100 dark:border-white/5">
                <img
                  :src="getOptimizedImageUrl(recentArticles[0].cover_image, 800)"
                  :alt="recentArticles[0].title"
                  loading="lazy"
                  decoding="async"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
                <div class="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-2xl pointer-events-none"></div>
              </div>
            </div>

            <!-- Right: Magazine Editorial Spread -->
            <div class="lg:col-span-7 flex flex-col justify-between py-2 lg:py-4">
              <div>
                <!-- Meta Row (Cleanly outside the image so artwork text is never blocked) -->
                <div class="flex flex-wrap items-center gap-2.5 mb-5">
                  <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#0b533b] text-white text-[11px] font-extrabold tracking-wider uppercase shadow-sm shadow-emerald-950/20">
                    <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    FEATURED STORY #01
                  </span>

                  <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 text-xs font-bold">
                    <svg class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {{ formatArticleDate(recentArticles[0].published_at || recentArticles[0].created_at) }}
                  </span>

                  <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 text-xs font-bold border border-emerald-200/60 dark:border-emerald-800/50">
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    อ่านประมาณ 4 นาที
                  </span>
                </div>

                <!-- Headline -->
                <h3 class="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 dark:text-white leading-[1.25] mb-4 group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">
                  {{ recentArticles[0].title }}
                </h3>

                <!-- Excerpt -->
                <p class="text-gray-600 dark:text-gray-300 text-sm sm:text-base lg:text-lg leading-relaxed line-clamp-3 mb-6">
                  {{ recentArticles[0].excerpt || recentArticles[0].seo_description || 'อ่านรายละเอียดเชิงลึก เทคนิคการเลือกขนาดและวัสดุ รวมถึงข้อแนะนำจากทีมวิศวกรผู้เชี่ยวชาญได้ภายในบทความนี้...' }}
                </p>

                <!-- Topic Highlights -->
                <div class="flex flex-wrap gap-2 mb-8">
                  <span class="px-3 py-1 rounded-full bg-gray-100/90 dark:bg-white/5 text-gray-600 dark:text-gray-400 text-xs font-semibold">#คู่มือเลือกซื้อ</span>
                  <span class="px-3 py-1 rounded-full bg-gray-100/90 dark:bg-white/5 text-gray-600 dark:text-gray-400 text-xs font-semibold">#เจาะลึกสเปควัสดุ</span>
                  <span class="px-3 py-1 rounded-full bg-gray-100/90 dark:bg-white/5 text-gray-600 dark:text-gray-400 text-xs font-semibold">#ไอเดียจัดพื้นที่บ้าน</span>
                </div>
              </div>

              <!-- Bottom Action Footer -->
              <div class="pt-5 border-t border-gray-100 dark:border-white/10 flex flex-wrap items-center justify-between gap-4">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 flex items-center justify-center font-black text-xs border border-emerald-200 dark:border-emerald-700/50">
                    OL
                  </div>
                  <div>
                    <p class="text-xs font-extrabold text-gray-900 dark:text-white">ทีมผู้เชี่ยวชาญ Outdoor Lander</p>
                    <p class="text-[11px] text-gray-500 dark:text-gray-400">Outdoor Living & Structure Specialist</p>
                  </div>
                </div>

                <span class="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 group-hover:bg-emerald-600 group-hover:text-white text-sm font-bold transition-all duration-300 shadow-sm">
                  อ่านบทความฉบับเต็ม
                  <svg class="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </router-link>

        <!-- 2. Secondary Articles Row (Horizontal Split Bento Cards with 1:1 Uncropped Thumbnails) -->
        <div v-if="recentArticles.length > 1" class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <router-link
            v-for="(article, idx) in recentArticles.slice(1)"
            :key="article.id"
            :to="'/blog/' + article.slug"
            class="group flex flex-col sm:flex-row items-stretch bg-white dark:bg-[#151f30] rounded-[1.75rem] p-4 sm:p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.25)] hover:shadow-[0_16px_45px_rgb(16,185,129,0.12)] border border-gray-200/80 dark:border-white/10 hover:border-emerald-500/40 transition-all duration-500 hover:-translate-y-1 gap-5 sm:gap-6"
          >
            <!-- 1:1 Square Thumbnail (Uncropped infographic artwork) -->
            <div class="w-full sm:w-48 md:w-56 aspect-square shrink-0 rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 relative border border-gray-100 dark:border-white/5">
              <img
                :src="getOptimizedImageUrl(article.cover_image, 500)"
                :alt="article.title"
                loading="lazy"
                decoding="async"
                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
              />
            </div>

            <!-- Editorial Content -->
            <div class="flex-1 flex flex-col justify-between py-1 min-w-0">
              <div>
                <!-- Meta Badges Row -->
                <div class="flex flex-wrap items-center gap-2 mb-3">
                  <span class="px-2.5 py-0.5 rounded-md bg-emerald-100/80 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-[11px] font-black tracking-wider uppercase">
                    ARTICLE #0{{ idx + 2 }}
                  </span>
                  <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <svg class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {{ formatArticleDate(article.published_at || article.created_at) }}
                  </span>
                </div>

                <!-- Title -->
                <h3 class="font-black text-gray-900 dark:text-white text-lg md:text-xl mb-2.5 line-clamp-2 leading-snug group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">
                  {{ article.title }}
                </h3>

                <!-- Excerpt -->
                <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 line-clamp-2 sm:line-clamp-3 leading-relaxed mb-4">
                  {{ article.excerpt || article.seo_description || 'อ่านรายละเอียดเชิงลึกและเทคนิคต่างๆ เพิ่มเติมได้ภายในบทความนี้...' }}
                </p>
              </div>

              <!-- Footer Action -->
              <div class="pt-3.5 border-t border-gray-100 dark:border-white/5 flex items-center justify-between">
                <span class="text-xs sm:text-sm font-extrabold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                  อ่านรายละเอียดบทความ
                </span>
                <div class="w-9 h-9 rounded-xl bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-200 group-hover:bg-emerald-600 group-hover:text-white flex items-center justify-center transition-all duration-300 shadow-sm">
                  <svg class="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </section>



    <!-- FAQ Section -->
    <section v-if="homeShowFaq && faqItems.length > 0" class="py-20 md:py-28 bg-[#f8faf9] dark:bg-[#0a0f16] border-t border-slate-200/70 dark:border-white/5 relative overflow-hidden">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div class="text-center mb-14">
          <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-800 dark:text-emerald-300 text-xs font-black tracking-wider uppercase mb-3.5">
            <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>{{ sectionTitles.faqTitle || 'คำถามที่พบบ่อย' }}</span>
          </div>
          <h2 class="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 dark:text-white leading-[1.18]">
            {{ sectionTitles.faqHeading || 'สิ่งที่คุณอยากรู้' }}
          </h2>
          <p class="text-sm sm:text-base text-slate-600 dark:text-slate-400 mt-2.5 leading-relaxed">
            {{ sectionTitles.faqDesc || 'รวบรวมคำตอบสำหรับคำถามที่ลูกค้าสอบถามบ่อยที่สุด' }}
          </p>
        </div>

        <div class="space-y-3.5">
          <div 
            v-for="(item, fIdx) in faqItems" 
            :key="fIdx"
            class="rounded-2xl border transition-all duration-200 overflow-hidden"
            :class="item.open ? 'bg-white dark:bg-slate-900 border-emerald-500/50 shadow-md ring-1 ring-emerald-500/20' : 'bg-white/80 dark:bg-slate-900/60 border-slate-200/80 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'"
          >
            <button 
              type="button" 
              @click="toggleFaq(fIdx)" 
              class="w-full p-5 sm:p-6 text-left flex items-center justify-between gap-4 cursor-pointer focus:outline-none"
            >
              <span class="font-extrabold text-sm sm:text-base text-slate-900 dark:text-white flex items-center gap-3">
                <span class="w-6 h-6 rounded-lg bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 font-mono text-xs flex items-center justify-center shrink-0">
                  Q{{ fIdx + 1 }}
                </span>
                <span>{{ item.q }}</span>
              </span>
              <span 
                class="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 shrink-0 transition-transform duration-200"
                :class="{ 'rotate-180 text-emerald-600': item.open }"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/>
                </svg>
              </span>
            </button>

            <div v-show="item.open" class="px-5 pb-5 sm:px-6 sm:pb-6 text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-100 dark:border-slate-800 pt-4">
              {{ item.a }}
            </div>
          </div>
        </div>

      </div>
    </section>

    <!-- Final Call to Action — Modern Daylight Architectural Consultation & Quotation Concierge -->
    <section class="py-20 md:py-28 bg-gradient-to-b from-[#f1f5f9] via-white to-[#f8fafc] dark:from-[#0a0f18] dark:via-[#0d1522] dark:to-[#0a0f16] border-t border-gray-200/70 dark:border-white/5 relative overflow-hidden">
      <!-- Ambient Daylight Orbs -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <div class="absolute -top-24 left-1/4 w-[480px] h-[480px] bg-emerald-500/[0.07] dark:bg-emerald-500/10 rounded-full blur-[140px]"></div>
        <div class="absolute -bottom-24 right-1/4 w-[420px] h-[420px] bg-teal-500/[0.06] dark:bg-teal-500/10 rounded-full blur-[130px]"></div>
      </div>

      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
        <!-- Main Architectural Split Bento Card -->
        <div class="rounded-[2.5rem] bg-white dark:bg-[#131d2e] border border-emerald-200/80 dark:border-emerald-500/25 shadow-[0_25px_70px_-15px_rgba(16,185,129,0.14)] dark:shadow-2xl overflow-hidden">
          <div class="grid grid-cols-1 lg:grid-cols-12 items-stretch">

            <!-- Left 7 Cols: Editorial Pitch, 3-Step Process & Dual CTA -->
            <div class="lg:col-span-7 p-8 sm:p-10 md:p-14 lg:p-16 flex flex-col justify-between relative overflow-hidden">
              <!-- Subtle corner accent -->
              <div class="absolute -top-20 -left-20 w-64 h-64 bg-emerald-500/[0.05] rounded-full blur-3xl pointer-events-none"></div>

              <div>
                <!-- Eyebrow Badge -->
                <div class="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/15 border border-emerald-200/80 dark:border-emerald-500/30 text-emerald-800 dark:text-emerald-300 text-xs font-extrabold tracking-wider uppercase mb-6 shadow-2xs">
                  <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span>ARCHITECTURAL SPACE CONSULTATION</span>
                </div>

                <!-- Dynamic Title from Admin -->
                <h2
                  class="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 dark:text-white leading-[1.18] mb-5"
                  v-html="ctaTitle"
                ></h2>

                <!-- Dynamic Description from Admin -->
                <p
                  class="text-base sm:text-lg text-slate-600 dark:text-gray-300 leading-relaxed mb-8 max-w-2xl"
                  v-html="ctaDesc"
                ></p>

                <!-- 3-Step Fast Track Pills -->
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-9">
                  <div class="flex items-center gap-3 p-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.04] border border-slate-200/70 dark:border-white/10">
                    <span class="w-7 h-7 rounded-xl bg-[#0b533b] text-white text-xs font-black flex items-center justify-center shrink-0 shadow-xs">01</span>
                    <span class="text-xs font-bold text-slate-800 dark:text-gray-200 leading-snug">เลือกรุ่นหรือขนาดพื้นที่</span>
                  </div>
                  <div class="flex items-center gap-3 p-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.04] border border-slate-200/70 dark:border-white/10">
                    <span class="w-7 h-7 rounded-xl bg-[#0b533b] text-white text-xs font-black flex items-center justify-center shrink-0 shadow-xs">02</span>
                    <span class="text-xs font-bold text-slate-800 dark:text-gray-200 leading-snug">รับใบเสนอราคาได้ทันที</span>
                  </div>
                  <div class="flex items-center gap-3 p-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.04] border border-slate-200/70 dark:border-white/10">
                    <span class="w-7 h-7 rounded-xl bg-[#0b533b] text-white text-xs font-black flex items-center justify-center shrink-0 shadow-xs">03</span>
                    <span class="text-xs font-bold text-slate-800 dark:text-gray-200 leading-snug">จัดส่งและติดตั้งทั่วไทย</span>
                  </div>
                </div>
              </div>

              <!-- Primary & Secondary Action Buttons -->
              <div class="flex flex-wrap items-center gap-4 pt-6 border-t border-slate-100 dark:border-white/10">
                <router-link
                  :to="ctaButtonLink"
                  @click="trackCTA('final_cta_click')"
                  class="group relative inline-flex items-center justify-center gap-3 px-8 py-4 text-sm sm:text-base font-extrabold text-white bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-2xl shadow-lg shadow-emerald-600/25 hover:shadow-xl hover:shadow-emerald-600/35 transition-all duration-300 hover:-translate-y-0.5 overflow-hidden"
                >
                  <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <span class="relative z-10">{{ ctaButtonText }}</span>
                  <span class="relative z-10 w-7 h-7 rounded-xl bg-white/20 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </span>
                </router-link>

                <router-link
                  to="/space-calculator"
                  class="inline-flex items-center justify-center gap-2.5 px-6 py-4 text-sm sm:text-base font-bold text-slate-800 dark:text-white bg-slate-100 hover:bg-emerald-50 dark:bg-white/5 dark:hover:bg-white/10 border border-slate-200/80 dark:border-white/15 hover:border-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 rounded-2xl transition-all duration-300"
                >
                  <svg class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span>คำนวณพื้นที่จัดวางฟรี</span>
                </router-link>
              </div>
            </div>

            <!-- Right 5 Cols: Interactive Concierge Service Stack (Light Emerald Daylight Panel) -->
            <div class="lg:col-span-5 bg-gradient-to-br from-emerald-50/90 via-teal-50/50 to-slate-50 dark:from-emerald-950/40 dark:via-[#0f2419] dark:to-[#0b1812] border-t lg:border-t-0 lg:border-l border-emerald-100/90 dark:border-white/10 p-6 sm:p-8 md:p-10 flex flex-col justify-between gap-5 relative overflow-hidden">
              <div class="relative z-10">
                <div class="flex items-center justify-between mb-5">
                  <div>
                    <p class="text-[11px] font-extrabold uppercase tracking-widest text-emerald-700 dark:text-emerald-400">SMART CONCIERGE</p>
                    <h3 class="text-lg sm:text-xl font-black text-slate-900 dark:text-white">เลือกช่องทางเริ่มต้นที่สะดวกสำหรับคุณ</h3>
                  </div>
                  <div class="w-10 h-10 rounded-2xl bg-white dark:bg-white/10 border border-emerald-200/80 dark:border-white/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shadow-xs">
                    <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                </div>

                <!-- 3 Interactive Concierge Cards -->
                <div class="space-y-3.5">
                  <!-- Card 1: Online Quotation -->
                  <router-link
                    to="/quotation"
                    class="group flex items-start gap-4 p-4 rounded-2xl bg-white dark:bg-[#152331] border border-emerald-200/70 dark:border-white/10 hover:border-emerald-500 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
                  >
                    <div class="w-11 h-11 rounded-xl bg-emerald-100/80 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center justify-between gap-2 mb-0.5">
                        <h4 class="text-sm font-black text-slate-900 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">ออกใบเสนอราคาออนไลน์</h4>
                        <span class="px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[10px] font-extrabold">2 นาที</span>
                      </div>
                      <p class="text-xs text-slate-500 dark:text-gray-400 line-clamp-1">รองรับทั้งบุคคลธรรมดาและนิติบุคคล ดาวน์โหลดเอกสารได้ทันที</p>
                    </div>
                  </router-link>

                  <!-- Card 2: AI Smart Consultant -->
                  <router-link
                    to="/ai-consultant"
                    class="group flex items-start gap-4 p-4 rounded-2xl bg-white dark:bg-[#152331] border border-emerald-200/70 dark:border-white/10 hover:border-emerald-500 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
                  >
                    <div class="w-11 h-11 rounded-xl bg-teal-100/80 dark:bg-teal-500/20 text-teal-700 dark:text-teal-300 flex items-center justify-center shrink-0 group-hover:bg-teal-600 group-hover:text-white transition-colors">
                      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center justify-between gap-2 mb-0.5">
                        <h4 class="text-sm font-black text-slate-900 dark:text-white group-hover:text-teal-700 dark:group-hover:text-teal-400 transition-colors">ปรึกษา AI เลือกรุ่นที่เหมาะกับบ้านคุณ</h4>
                        <span class="px-2 py-0.5 rounded-md bg-teal-50 dark:bg-teal-500/15 text-teal-700 dark:text-teal-300 text-[10px] font-extrabold">AI MATCH</span>
                      </div>
                      <p class="text-xs text-slate-500 dark:text-gray-400 line-clamp-1">วิเคราะห์ขนาดพื้นที่ งบประมาณ และแนะนำรุ่นที่คุ้มค่าที่สุด</p>
                    </div>
                  </router-link>

                  <!-- Card 3: Talk to Engineering Team -->
                  <router-link
                    to="/contact"
                    class="group flex items-start gap-4 p-4 rounded-2xl bg-white dark:bg-[#152331] border border-emerald-200/70 dark:border-white/10 hover:border-emerald-500 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
                  >
                    <div class="w-11 h-11 rounded-xl bg-emerald-100/80 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center justify-between gap-2 mb-0.5">
                        <h4 class="text-sm font-black text-slate-900 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">พูดคุยกับทีมวิศวกร &amp; นัดดูโชว์รูม</h4>
                        <span class="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-gray-300 text-[10px] font-extrabold">ฟรีไม่มีค่าใช้จ่าย</span>
                      </div>
                      <p class="text-xs text-slate-500 dark:text-gray-400 line-clamp-1">ปรึกษาการเตรียมพื้นหน้างานและบริการติดตั้งทั่วประเทศ</p>
                    </div>
                  </router-link>
                </div>
              </div>

              <!-- Bottom Trust Guarantee Strip -->
              <div class="relative z-10 pt-4 border-t border-emerald-200/60 dark:border-white/10 flex items-center justify-between text-xs font-bold text-emerald-900 dark:text-emerald-300">
                <span class="inline-flex items-center gap-1.5">
                  <svg class="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  โครงสร้างแข็งแรงได้มาตรฐานสากล
                </span>
                <span class="text-emerald-700 dark:text-emerald-400">พร้อมบริการหลังการขายครบวงจร</span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>

    <!-- Affiliated Companies Section — Modern Corporate Group & Partner Ecosystem -->
    <section v-if="homeShowAffiliates && affiliatedCompanies.length > 0" class="py-20 md:py-28 bg-gradient-to-b from-white via-[#f8fafc] to-gray-100/80 dark:from-[#0a0f16] dark:via-[#0d1422] dark:to-[#0f172a] border-t border-gray-200/60 dark:border-white/5 relative overflow-hidden">
      <!-- Subtle Architectural Grid & Glow -->
      <div class="absolute inset-0 pointer-events-none overflow-hidden">
        <div class="absolute -top-32 left-1/4 w-[500px] h-[500px] bg-emerald-500/[0.04] dark:bg-emerald-500/[0.06] rounded-full blur-[140px]"></div>
        <div class="absolute -bottom-32 right-1/4 w-[450px] h-[450px] bg-teal-500/[0.04] dark:bg-teal-500/[0.06] rounded-full blur-[130px]"></div>
      </div>

      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
        <!-- Executive Section Header -->
        <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-10 mb-12 border-b border-gray-200/80 dark:border-white/10">
          <div class="max-w-2xl">
            <div class="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-emerald-950 text-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-300 text-xs font-bold tracking-widest uppercase mb-4 shadow-sm border border-emerald-800/40 dark:border-emerald-500/30">
              <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              {{ sectionTitles.affiliatesTitle }}
              <span class="text-emerald-500/60">|</span>
              <span class="text-[10px] text-emerald-400/90 font-semibold tracking-wider">CORPORATE ECOSYSTEM</span>
            </div>
            <h2 class="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.15]">
              {{ sectionTitles.affiliatesHeading }}
            </h2>
            <p class="mt-3 text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
              กลุ่มบริษัทในเครือและพันธมิตรทางธุรกิจที่พร้อมขับเคลื่อนนวัตกรรมวิศวกรรม อุตสาหกรรม และโซลูชันครบวงจรมาตรฐานสากล
            </p>
          </div>

          <!-- Corporate Ecosystem Badge -->
          <div class="flex items-center gap-4 self-start lg:self-end bg-white dark:bg-[#151f30] px-5 py-3.5 rounded-2xl border border-gray-200/80 dark:border-white/10 shadow-sm">
            <div class="w-11 h-11 rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
              <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <p class="text-xs font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Official Business Group</p>
              <p class="text-sm font-black text-gray-900 dark:text-white">{{ affiliatedCompanies.length }} บริษัทในเครือข่ายพันธมิตร</p>
            </div>
          </div>
        </div>

        <!-- Corporate Partner Cards Grid -->
        <div
          class="grid gap-8"
          :class="affiliatedCompanies.length === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'"
        >
          <a
            v-for="(company, idx) in affiliatedCompanies"
            :key="idx"
            :href="company.url"
            target="_blank"
            rel="noopener noreferrer"
            class="group flex flex-col rounded-[2rem] overflow-hidden bg-white dark:bg-[#151f30] shadow-[0_10px_35px_rgb(0,0,0,0.05)] dark:shadow-[0_10px_35px_rgb(0,0,0,0.3)] hover:shadow-[0_20px_50px_rgb(16,185,129,0.14)] transition-all duration-500 hover:-translate-y-1.5 border border-gray-200/80 dark:border-white/10 hover:border-emerald-500/40"
          >
            <!-- Top Banner Frame (2:1 proportion so banner text & graphics are never cropped) -->
            <div class="relative aspect-[2/1] w-full overflow-hidden bg-gray-900 border-b border-gray-100 dark:border-white/5">
              <img
                :src="getOptimizedImageUrl(company.banner, 1200)"
                :alt="company.name"
                loading="lazy"
                decoding="async"
                class="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
              />
              <!-- Subtle vignette on hover -->
              <div class="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

              <!-- Top-left Partner Index Pill -->
              <div class="absolute top-4 left-4 px-3 py-1 rounded-full bg-gray-950/75 backdrop-blur-md text-white text-[11px] font-extrabold tracking-wider uppercase border border-white/15 shadow-md flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                PARTNER #{{ String(idx + 1).padStart(2, '0') }}
              </div>
            </div>

            <!-- Bottom Corporate Profile Content -->
            <div class="p-6 sm:p-7 md:p-8 flex-1 flex flex-col justify-between gap-6">
              <div>
                <!-- Title & Verified Pill -->
                <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h3 class="text-xl sm:text-2xl font-black text-gray-900 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors flex items-center gap-2.5">
                    <span>{{ company.name }}</span>
                    <span class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" title="Verified Partner">
                      <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </span>
                  </h3>

                  <!-- Domain Pill -->
                  <span v-if="company.url" class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 text-xs font-mono font-semibold border border-gray-200/60 dark:border-white/10">
                    <svg class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    {{ company.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '') }}
                  </span>
                </div>

                <!-- Full Multi-line Description -->
                <p v-if="company.description" class="text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed line-clamp-3">
                  {{ company.description }}
                </p>
              </div>

              <!-- Action Footer Bar -->
              <div class="pt-5 border-t border-gray-100 dark:border-white/10 flex items-center justify-between gap-4">
                <div class="flex items-center gap-2 text-xs font-bold text-gray-500 dark:text-gray-400">
                  <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                  <span>พันธมิตรและบริษัทในเครืออย่างเป็นทางการ</span>
                </div>

                <span class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 dark:bg-white/10 text-white group-hover:bg-emerald-600 text-xs sm:text-sm font-bold transition-all duration-300 shadow-sm shrink-0">
                  <span>เยี่ยมชมเว็บไซต์</span>
                  <svg class="w-4 h-4 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </span>
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>

  </div>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.fade-slide-up-enter-active, .fade-slide-up-leave-active {
  transition: all 0.6s ease;
}
.fade-slide-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.fade-slide-up-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Cinematic Bento Showcase Custom Keyframes & Animations */
@keyframes float {
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(1deg); }
  100% { transform: translateY(0px) rotate(0deg); }
}
.animate-float {
  animation: float 6s ease-in-out infinite;
}

@keyframes podium-glow {
  0%, 100% {
    transform: scale(1) translateY(0);
    box-shadow: 0 0 30px rgba(16, 185, 129, 0.4), inset 0 0 15px rgba(16, 185, 129, 0.2);
    opacity: 0.4;
  }
  50% {
    transform: scale(1.02) translateY(1px);
    box-shadow: 0 0 55px rgba(16, 185, 129, 0.7), inset 0 0 25px rgba(16, 185, 129, 0.4);
    opacity: 0.6;
  }
}
.animate-podium-glow {
  animation: podium-glow 4s ease-in-out infinite;
}

/* Hero Fold Full Viewport Height */
.hero-fold-section {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

@media (min-width: 1024px) {
  .hero-fold-section {
    min-height: calc(100vh - 106px);
    min-height: calc(100dvh - 106px);
  }
}
</style>

